import { useEffect, useMemo, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useEditAccessRequest } from "../components/EditAccessRequest";
import { Button } from "../components/ui/button";
import { PageHeader } from "../components/GradientCard";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { tabAccessibleFor, subtabAccessibleFor } from "../lib/modules";
import TrainerProjectsTab from "../components/TrainerProjectsTab";
import TravelAdvanceFormDialog from "../components/TravelAdvanceFormDialog";
import { useGlobalViewMode } from "../components/ViewModeToggle";
import UserPickerDialog from "../components/UserPickerDialog";
import { toast } from "sonner";
import { Plus, MapPin, Phone, User, Calendar, Plane, GraduationCap, Building2, Search, ShieldCheck, ClipboardCheck, Users, UserCog, Send, CheckCircle2, XCircle } from "lucide-react";

import { fmtDateStr } from "../lib/datetime";

const TRAINING_TYPES = ["Demo", "Installation", "Support", "Other"];
const STATUS_COLOR = {
  "Upcoming": "bg-slate-100 text-slate-700 ring-1 ring-slate-200",
  "Assigned": "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200",
  "In Progress": "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  "Completed": "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
};

// Universal indigo tab styling — reused for both top-level and sub-tabs.
const TAB_TRIG_CLS = "data-[state=active]:!bg-indigo-600 data-[state=active]:!text-white data-[state=active]:!shadow-sm text-slate-700 dark:text-slate-200";
const SUBTAB_TRIG_CLS = "data-[state=active]:!bg-slate-900 data-[state=active]:!text-white data-[state=active]:!shadow-sm text-slate-600 dark:text-slate-300 text-[11.5px]";

const STATUS_STYLES = {
  "Free Today": "bg-emerald-100 text-emerald-700",
  "In Office": "bg-emerald-100 text-emerald-700",
  "Working": "bg-violet-100 text-violet-700",
  "Punched Out": "bg-slate-100 text-slate-600",
  "Not Punched In": "bg-rose-50 text-rose-700 border border-rose-200",
  "On Leave": "bg-rose-100 text-rose-700",
  "In Training": "bg-amber-100 text-amber-800",
  "In Travel": "bg-blue-100 text-blue-700",
  default: "bg-slate-100 text-slate-600",
};

const ATT_ICON = {
  "Working": "🟣",
  "Punched Out": "⚪",
  "Not Punched In": "⚫",
};
const WORK_ICON = {
  "In Office": "🟢",
  "On Leave": "🔴",
  "In Training": "🟡",
  "In Travel": "🔵",
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  } catch { return iso; }
};

export default function Trainers() {
  const { user } = useAuth();
  const role = user?.role;
  const isMaster = role === "master_admin";
  // v109.9.9 — Save-and-request for trainer edits.
  const { dialog: trReqDialog, wrapMutation: wrapTrReq } = useEditAccessRequest();
  const isAdmin = ["master_admin", "sales_admin", "crm_manager"].includes(role);
  const isTrainer = role === "trainer";
  // v107 — 4 tabs. Sub-tabs live inside the Training tab.
  const [tab, setTab] = useState("trainer");
  const [trainingSub, setTrainingSub] = useState(isTrainer ? "assigned" : "upcoming");
  const [trainers, setTrainers] = useState([]);
  const [statusMap, setStatusMap] = useState({});
  const [assignments, setAssignments] = useState([]);
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [activeAssignment, setActiveAssignment] = useState(null);
  const [travelInput, setTravelInput] = useState("");
  // v107 — training requests + tab access config
  const [reqOpen, setReqOpen] = useState(false);
  const [reqForm, setReqForm] = useState(emptyReqForm());
  const [pendingReqs, setPendingReqs] = useState([]);
  const [myReqs, setMyReqs] = useState([]);
  const [trainersCfg, setTrainersCfg] = useState({ tab_access: { trainer: [], training: [], projects: [], approval: [] }, all_employees: [] });
  const [tabPicker, setTabPicker] = useState(null); // 'trainer' | 'training' | 'projects' | 'approval'
  // v109.9.40 — Tab Access chooser removed; stub retained to avoid rippling changes.
  const tabPickerOpen = false; const setTabPickerOpen = () => {};
  void tabPickerOpen; void setTabPickerOpen;
  const [trainerVm] = useGlobalViewMode();
  // v108 — Active employees for "Who assigned this training" dropdown
  const [activeEmployees, setActiveEmployees] = useState([]);
  // v108 — Arrival check-in queue (auto-modal on page load for trainers)
  const [arrivalQueue, setArrivalQueue] = useState([]);
  // v109.9.165 — Apply Advance from an assigned training
  const [applyAdvanceFor, setApplyAdvanceFor] = useState(null);
  // v108 — Approval-tab open/edit dialog
  const [approvalDetail, setApprovalDetail] = useState(null);
  // v108 — Completion-upload dialog for Mark Complete
  const [completeDialog, setCompleteDialog] = useState(null);
  // v109 — Sales Associate reveal for the currently opened Assigned/In-Progress training
  const [activeSalesAssoc, setActiveSalesAssoc] = useState(null);
  const uid = user?.id;
  const ta = trainersCfg.tab_access || {};
  // v109.9.160 — Module-level Access-button grants (effective_permissions.trainers)
  // open ALL trainer tabs & data. Without this, users who were granted the
  // Trainers module via Master Access still hit empty tabs because the legacy
  // per-tab `tab_access` config wasn't updated for them. Master Admin bypass
  // stays; the granular `tab_access` list still works as an additive grant.
  const modEff = user?.effective_permissions?.trainers || null;
  const hasModuleGrant = isMaster || !!(modEff && (modEff.view || modEff.edit || modEff.delete));
  const canApproval = hasModuleGrant || (uid && (ta.approval || []).includes(uid));
  const canProjects = hasModuleGrant || (uid && (ta.projects || []).includes(uid));
  // v107 — Trainer tab was strictly gated by tab_access.trainer. From v109.9.160
  // a module-level grant now bypasses that requirement so Access-button grants
  // actually surface data.
  const canTrainer = hasModuleGrant || (uid && (ta.trainer || []).includes(uid));
  // v109 — Training sub-tab granular access. If nothing is explicitly configured
  // for a sub-tab, everyone with parent Training access sees it. If a sub-tab
  // has an explicit list, only those users see that sub-tab (plus MA / module grant).
  const _subCfg = (key) => (ta[key] || []).map(String);
  const canSubUpcoming  = hasModuleGrant || _subCfg("training_upcoming").length === 0  || (uid && _subCfg("training_upcoming").includes(uid));
  const canSubAssigned  = hasModuleGrant || _subCfg("training_assigned").length === 0  || (uid && _subCfg("training_assigned").includes(uid));
  const canSubCompleted = hasModuleGrant || _subCfg("training_completed").length === 0 || (uid && _subCfg("training_completed").includes(uid));

  // If the user lands on the Trainer tab without access, bounce them to Training.
  useEffect(() => {
    if (tab === "trainer" && !canTrainer) setTab("training");
    if (tab === "approval" && !canApproval && !isMaster) { /* keep — MA sees own requests */ }
  }, [tab, canTrainer, canApproval, isMaster]);

  const load = async () => {
    try {
      const requests = [api.get("/trainer-assignments")];
      if (!isTrainer) requests.unshift(api.get("/trainers"));
      const results = await Promise.all(requests);
      if (!isTrainer) {
        setTrainers(results[0].data);
        setAssignments(results[1].data);
      } else {
        setTrainers([]);
        setAssignments(results[0].data);
      }
      if (!isTrainer) {
        try {
          const { data } = await api.get("/trainers/status-today");
          const m = {};
          (data || []).forEach((s) => { m[s.id] = s; });
          setStatusMap(m);
        } catch { setStatusMap({}); }
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (isAdmin) api.get("/records/crm_clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [isAdmin]);
  // v108 — Load active employees list for the "Who assigned this training" dropdown
  useEffect(() => {
    api.get("/employees-lite").then(({ data }) => setActiveEmployees(data || [])).catch(() => setActiveEmployees([]));
  }, []);
  // v108 — Pending arrival check-ins (auto-modal for trainers)
  useEffect(() => {
    if (!isTrainer) return;
    api.get("/trainer-assignments/pending-arrival-checks")
      .then(({ data }) => setArrivalQueue(data || []))
      .catch(() => setArrivalQueue([]));
  }, [isTrainer, assignments]);
  // v108 — When user opens the request dialog, default "Who assigned" = current user
  useEffect(() => {
    if (reqOpen && !reqForm.assigned_by_id && user?.id) {
      setReqForm((f) => ({ ...f, assigned_by_id: user.id, assigned_by_name: user.name || "" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqOpen]);
  // v109 — Fetch sales associate details when Assigned/In-Progress training is opened
  useEffect(() => {
    if (!activeAssignment || activeAssignment.status === "Upcoming") { setActiveSalesAssoc(null); return; }
    let alive = true;
    api.get(`/trainer-assignments/${activeAssignment.id}/sales-associate`)
      .then(({ data }) => { if (alive) setActiveSalesAssoc(data); })
      .catch(() => { if (alive) setActiveSalesAssoc(null); });
    return () => { alive = false; };
  }, [activeAssignment]);

  // v107 — Trainers tab-access config + training-request queues
  const loadTrainersMeta = async () => {
    try {
      const { data } = await api.get("/trainers/config");
      setTrainersCfg({
        tab_access: data.tab_access || { trainer: [], training: [], projects: [], approval: [] },
        all_employees: data.all_employees || [],
      });
    } catch { /* silent */ }
    try {
      const { data } = await api.get("/training-requests", { params: { scope: "mine" } });
      setMyReqs(data || []);
    } catch { /* silent */ }
    if (canApproval) {
      try {
        const { data } = await api.get("/training-requests", { params: { scope: "pending" } });
        setPendingReqs(data || []);
      } catch { /* silent */ }
    }
  };
  useEffect(() => { loadTrainersMeta(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [uid]);
  useEffect(() => {
    if (canApproval) {
      api.get("/training-requests", { params: { scope: "pending" } }).then(({ data }) => setPendingReqs(data || [])).catch(() => {});
    }
  }, [canApproval]);

  const submitTrainingRequest = async () => {
    if (!reqForm.client_placeholder && !reqForm.client_id) {
      toast.error("Please pick a School / Client from the list — or tick 'Master Admin will auto-fill'");
      return;
    }
    if (!reqForm.start_datetime || !reqForm.end_datetime) { toast.error("Start and End date+time are required"); return; }
    if (reqForm.location_map_url) {
      const u = reqForm.location_map_url.toLowerCase();
      const ok = /^https?:\/\//.test(u) && (u.includes("google.com/maps") || u.includes("maps.google.com") || u.includes("goo.gl/maps") || u.includes("maps.app.goo.gl"));
      if (!ok) { toast.error("Location must be a valid Google Maps link"); return; }
    }
    if (reqForm.report_to_is_new && (!reqForm.report_to_name || !reqForm.report_to_phone)) {
      toast.error("For a new Report-To contact, both name and phone are required"); return;
    }
    try {
      await api.post("/training-requests", reqForm);
      toast.success("Training request submitted");
      setReqOpen(false);
      setReqForm(emptyReqForm());
      loadTrainersMeta();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const decideRequest = async (req, decision) => {
    if (decision === "reject") {
      const reason = window.prompt(`Reject request "${req.title}"? Enter reason:`);
      if (!reason || !reason.trim()) { toast.error("Reason is required"); return; }
      try {
        await api.post(`/training-requests/${req.id}/reject`, { reason: reason.trim() });
        toast.success("Rejected");
        loadTrainersMeta();
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    } else {
      try {
        // v109.9.160 — Placeholder request: force Master Admin to pick a
        // real School/Client from CRM before the backend will approve.
        let extra = {};
        if (req.client_placeholder) {
          const q = window.prompt(
            "This request is a placeholder (\"Master Admin will auto-fill\").\n" +
            "Paste the CRM Client ID to link this training to.\n\n" +
            "Tip: open Client Database → click any row → copy the Client ID.",
            ""
          );
          if (!q || !q.trim()) { toast.error("Client ID is required to approve a placeholder request"); return; }
          extra = { client_id: q.trim() };
        }
        await api.post(`/training-requests/${req.id}/approve`, extra);
        toast.success("Approved — now visible in Upcoming Trainings");
        loadTrainersMeta();
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    }
  };

  const saveTabAccess = async (key, userIds) => {
    if (!isMaster) return;
    const next = { ...(trainersCfg.tab_access || {}), [key]: userIds };
    // v109 — Optimistic UI: reflect the toggle click instantly.
    setTrainersCfg((prev) => ({ ...prev, tab_access: next }));
    try {
      const { data } = await api.patch("/trainers/config", { tab_access: next });
      setTrainersCfg({
        tab_access: data.tab_access || next,
        all_employees: data.all_employees || trainersCfg.all_employees,
      });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
      // Roll back if the API rejected the change.
      loadTrainersMeta();
    }
  };

  const filteredTrainers = useMemo(() => {
    const q = search.toLowerCase();
    return trainers.filter((t) => !q || [t.name, t.email, t.employee_code, t.current_assignment?.client_name].some((v) => String(v ?? "").toLowerCase().includes(q)));
  }, [trainers, search]);

  // v107 — Training visibility scoping (Feb 2026 spec).
  //   • Master Admin + users in tab_access.training → see all trainings.
  //   • Trainer role → see only trainings assigned to them (both active + completed).
  //   • Other module users → see only trainings they raised themselves.
  //   • Upcoming pool is visible to everyone so trainers/users can request.
  const canSeeAllTrainings = hasModuleGrant || (uid && (ta.training || []).includes(uid));
  const scopedAssignments = useMemo(() => {
    if (canSeeAllTrainings) return assignments;
    if (isTrainer) return assignments.filter((a) => a.trainer_id === uid || a.status === "Upcoming");
    return assignments.filter((a) => a.created_by_id === uid || a.status === "Upcoming");
  }, [assignments, canSeeAllTrainings, isTrainer, uid]);
  const upcomingAssignments = useMemo(() => scopedAssignments.filter((a) => a.status === "Upcoming"), [scopedAssignments]);
  const activeAssignments = useMemo(() => scopedAssignments.filter((a) => a.status === "Assigned" || a.status === "In Progress"), [scopedAssignments]);
  const completedAssignments = useMemo(() => scopedAssignments.filter((a) => a.status === "Completed"), [scopedAssignments]);

  const submit = async () => {
    if (!form.client_id) { toast.error("Pick a client"); return; }
    if (!form.training_from || !form.training_to) { toast.error("Training dates are required"); return; }
    if (form.training_type === "Other" && !form.type_other_name.trim()) { toast.error("Name the training type"); return; }
    try {
      if (form._editing_id) {
        const { _editing_id, client_id, ...rest } = form;
        const result = await wrapTrReq(
          () => api.patch(`/trainer-assignments/${_editing_id}`, rest),
          {
            moduleKey: "trainers",
            recordId: _editing_id,
            recordLabel: form.client_name || form.training_type || _editing_id,
            proposedPayload: rest,
            stageOn403: true,
          },
        );
        if (!result) { setOpen(false); setForm(emptyForm()); return; }
        toast.success("Training updated");
      } else {
        await api.post("/trainer-assignments", form);
        toast.success(form.trainer_id ? "Trainer assigned" : "Upcoming training created — trainers can now request it");
      }
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const requestTraining = async (a, note = "") => {
    try {
      await api.post(`/trainer-assignments/${a.id}/request`, { note });
      toast.success("Request submitted");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  // v107 — Trainer clicks the single "Request Training" button → opens a
  // details+remark dialog. Same form is used whether they clicked "Open" or
  // "Request", per user's Feb 2026 spec.
  const [requestingRow, setRequestingRow] = useState(null);
  const [requestingNote, setRequestingNote] = useState("");

  const approveRequest = async (a, req) => {
    if (!window.confirm(`Approve ${req.trainer_name} for ${a.client_name}?`)) return;
    try {
      const { data } = await api.post(`/trainer-assignments/${a.id}/approve-request`, { trainer_id: req.trainer_id });
      setActiveAssignment(data);
      toast.success(`${req.trainer_name} assigned`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const rejectRequest = async (a, req) => {
    if (!window.confirm(`Reject ${req.trainer_name}'s request?`)) return;
    try {
      const { data } = await api.post(`/trainer-assignments/${a.id}/reject-request`, { trainer_id: req.trainer_id });
      setActiveAssignment(data);
      toast.success(`${req.trainer_name}'s request rejected`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const directAssign = async (a, trainerId) => {
    if (!trainerId) return;
    try {
      const { data } = await api.patch(`/trainer-assignments/${a.id}`, { trainer_id: trainerId, status: "Assigned" });
      setActiveAssignment(data);
      toast.success(`Assigned ${data.trainer_name}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const openEditUpcoming = (a) => {
    setForm({
      _editing_id: a.id,
      client_id: a.client_id || "",
      trainer_id: a.trainer_id || "",
      training_type: a.training_type || "Demo",
      type_other_name: a.type_other_name || "",
      training_from: a.training_from || "",
      training_to: a.training_to || "",
      contact_name: a.contact_name || "",
      contact_phone: a.contact_phone || "",
      contact_email: a.contact_email || "",
      remark: a.remark || "",
    });
    setOpen(true);
  };

  const addTravelDate = async () => {
    if (!travelInput) return;
    const newDates = [...(activeAssignment.travel_dates || []), travelInput].sort();
    try {
      const { data } = await api.patch(`/trainer-assignments/${activeAssignment.id}`, { travel_dates: newDates });
      setActiveAssignment(data); setTravelInput(""); load();
      toast.success("Travel date added");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const confirmDates = async (a) => {
    if (!window.confirm(`Confirm with the school that the training will run from ${a.training_from} to ${a.training_to}?`)) return;
    try {
      const { data } = await api.post(`/trainer-assignments/${a.id}/confirm-dates`);
      setActiveAssignment(data); load();
      toast.success("Dates confirmed — you can now manage travel dates and use your one edit.");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const [dateEditDialog, setDateEditDialog] = useState(null); // {mode:"edit"|"request", from, to, remark}
  const openEditDates = (a, mode) => {
    setDateEditDialog({
      mode,
      training_from: a.training_from || "",
      training_to: a.training_to || "",
      remark: "",
    });
  };
  const submitEditDates = async () => {
    if (!dateEditDialog) return;
    const { mode, training_from, training_to, remark } = dateEditDialog;
    if (!training_from || !training_to) { toast.error("Both dates are required"); return; }
    if (!remark.trim()) { toast.error("Please add a remark explaining the change"); return; }
    if (training_from > training_to) { toast.error("From must be on or before To"); return; }
    try {
      const url = mode === "request"
        ? `/trainer-assignments/${activeAssignment.id}/request-date-change`
        : `/trainer-assignments/${activeAssignment.id}/edit-dates`;
      const payload = mode === "request"
        ? { training_from, training_to, reason: remark }
        : { training_from, training_to, remark };
      const { data } = await api.post(url, payload);
      setActiveAssignment(data); setDateEditDialog(null); load();
      toast.success(mode === "request" ? "Date-change request sent to Master Admin" : "Dates updated");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const decideDateRequest = async (req, decision) => {
    const remark = window.prompt(
      `${decision} request from ${req.requested_by_name}?\n\n` +
      `Requested: ${req.requested_from} → ${req.requested_to}\nReason: ${req.reason}\n\n` +
      (decision === "Approved" ? "Approving will apply these dates. Optional remark:" : "Please provide a reason for rejection:"),
      "",
    );
    if (remark === null) return;
    if (decision === "Rejected" && !remark.trim()) { toast.error("Rejection requires a remark"); return; }
    try {
      const { data } = await api.post(
        `/trainer-assignments/${activeAssignment.id}/decide-date-change/${req.id}`,
        { decision, remark },
      );
      setActiveAssignment(data); load();
      toast.success(`Request ${decision.toLowerCase()}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removeTravelDate = async (d) => {
    const newDates = (activeAssignment.travel_dates || []).filter((x) => x !== d);
    try {
      const { data } = await api.patch(`/trainer-assignments/${activeAssignment.id}`, { travel_dates: newDates });
      setActiveAssignment(data); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const updateStatus = async (newStatus) => {
    try {
      const { data } = await api.patch(`/trainer-assignments/${activeAssignment.id}`, { status: newStatus });
      setActiveAssignment(data); load();
      toast.success(`Marked ${newStatus}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removeAssignment = async (a) => {
    if (!window.confirm(`Delete training for ${a.client_name}?`)) return;
    try {
      await api.delete(`/trainer-assignments/${a.id}`);
      toast.success("Removed"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-[1500px] space-y-6">
      <PageHeader
        moduleKey="trainers"
        tone="indigo"
        icon={GraduationCap}
        eyebrow="Training Module"
        title="Trainers"
        subtitle="Trainers, training pipeline, project & activity ledger, and approval queue — all in one place."
        actions={(
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={() => { setReqForm(emptyReqForm()); setReqOpen(true); }} variant="outline" className="h-10 border-indigo-200 text-indigo-700 hover:bg-indigo-50" data-testid="tr-raise-req">
              <Send size={14} className="mr-1.5" /> Raise Training Request
            </Button>
          </div>
        )}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-1 flex-wrap h-auto gap-1">
          {/* v109.9.66 — Universal Law: Master Access decides tab visibility. */}
          {tabAccessibleFor(user, "trainers", "trainer") && <TabsTrigger value="trainer" className={TAB_TRIG_CLS} data-testid="tr-tab-trainer">Trainer ({trainers.length})</TabsTrigger>}
          {tabAccessibleFor(user, "trainers", "training") && <TabsTrigger value="training" className={TAB_TRIG_CLS} data-testid="tr-tab-training">Training ({assignments.length})</TabsTrigger>}
          {tabAccessibleFor(user, "trainers", "projects") && <TabsTrigger value="projects" className={TAB_TRIG_CLS} data-testid="tr-tab-projects">Project &amp; Activity</TabsTrigger>}
          {tabAccessibleFor(user, "trainers", "approval") && (
            <TabsTrigger value="approval" className={TAB_TRIG_CLS} data-testid="tr-tab-approval">
              Approval{canApproval && pendingReqs.length ? ` (${pendingReqs.length})` : ""}
            </TabsTrigger>
          )}
        </TabsList>

        {/* ============================================================
             TAB 1 · TRAINER (existing trainer team roster + statuses)
             ============================================================ */}
        {tabAccessibleFor(user, "trainers", "trainer") && (
        <TabsContent value="trainer" className="mt-4 space-y-4">
          {!isTrainer && (
            <div className="relative max-w-md">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search trainers…" className="pl-9 h-10" data-testid="tr-search" />
            </div>
          )}
          {trainerVm === "card" ? (
            <TrainerCardsGrid trainers={filteredTrainers} statusMap={statusMap} />
          ) : (
            <TrainersListTable trainers={filteredTrainers} statusMap={statusMap} />
          )}
        </TabsContent>
        )}

        {/* ============================================================
             TAB 2 · TRAINING (Upcoming | Assigned | Completed sub-tabs)
             ============================================================ */}
        <TabsContent value="training" className="mt-4">
          <Tabs value={trainingSub} onValueChange={setTrainingSub}>
            <TabsList className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-1 h-auto gap-1">
              {subtabAccessibleFor(user, "trainers", "training", "upcoming") && <TabsTrigger value="upcoming" className={SUBTAB_TRIG_CLS} data-testid="tr-sub-upcoming">Upcoming ({upcomingAssignments.length})</TabsTrigger>}
              {subtabAccessibleFor(user, "trainers", "training", "assigned") && (
                <TabsTrigger value="assigned" className={SUBTAB_TRIG_CLS} data-testid="tr-sub-assigned">
                  {isTrainer ? "My Active" : "Assigned"} ({activeAssignments.length})
                </TabsTrigger>
              )}
              {subtabAccessibleFor(user, "trainers", "training", "completed") && <TabsTrigger value="completed" className={SUBTAB_TRIG_CLS} data-testid="tr-sub-completed">Completed ({completedAssignments.length})</TabsTrigger>}
            </TabsList>

            <TabsContent value="upcoming" className="mt-4 space-y-4">
              {trainerVm === "card" ? (
                <AssignmentCards
                  rows={upcomingAssignments}
                  isAdmin={isAdmin}
                  isTrainer={isTrainer}
                  user={user}
                  onOpen={setActiveAssignment}
                  onEdit={openEditUpcoming}
                  onDelete={removeAssignment}
                  onRequest={(a) => { setRequestingRow(a); setRequestingNote(""); }}
                  variant="upcoming"
                />
              ) : (
                <UpcomingTable
                  rows={upcomingAssignments} isAdmin={isAdmin} isTrainer={isTrainer} user={user}
                  onOpen={setActiveAssignment} onEdit={openEditUpcoming} onDelete={removeAssignment}
                  onRequest={(a) => { setRequestingRow(a); setRequestingNote(""); }}
                />
              )}
            </TabsContent>
            <TabsContent value="assigned" className="mt-4">
              {trainerVm === "card" ? (
                <AssignmentCards
                  rows={activeAssignments}
                  isAdmin={isAdmin}
                  isTrainer={isTrainer}
                  user={user}
                  onOpen={setActiveAssignment}
                  onDelete={removeAssignment}
                  variant="active"
                />
              ) : (
                <AssignmentTable rows={activeAssignments} isAdmin={isAdmin} onOpen={(a) => setActiveAssignment(a)} onDelete={removeAssignment} testidPrefix="active" />
              )}
            </TabsContent>
            <TabsContent value="completed" className="mt-4">
              {trainerVm === "card" ? (
                <AssignmentCards
                  rows={completedAssignments}
                  isAdmin={isAdmin}
                  isTrainer={isTrainer}
                  user={user}
                  onOpen={setActiveAssignment}
                  onDelete={removeAssignment}
                  variant="completed"
                />
              ) : (
                <AssignmentTable rows={completedAssignments} isAdmin={isAdmin} onOpen={(a) => setActiveAssignment(a)} onDelete={removeAssignment} testidPrefix="completed" />
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ============================================================
             TAB 3 · PROJECT & ACTIVITY
             ============================================================ */}
        <TabsContent value="projects" className="mt-4">
          <TrainerProjectsTab trainerList={trainers} canApprove={canApproval} viewMode={trainerVm} />
        </TabsContent>

        {/* ============================================================
             TAB 4 · APPROVAL (MA + tab_access.approval)
             ============================================================ */}
        <TabsContent value="approval" className="mt-4">
          <ApprovalQueue
            canApproval={canApproval}
            pendingReqs={pendingReqs}
            myReqs={myReqs}
            onDecide={decideRequest}
            onReload={loadTrainersMeta}
            activeEmployees={activeEmployees}
            trainers={trainers}
          />
        </TabsContent>
      </Tabs>


      {/* New Assignment Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <div className="relative bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center">
                <GraduationCap size={20} className="text-indigo-700 dark:text-indigo-300" />
              </div>
              <div>
                <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100">{form._editing_id ? "Edit Training" : "New Training"}</DialogTitle>
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">Pick a trainer to assign immediately, or leave it blank to create an <span className="font-semibold underline">Upcoming</span> training that trainers can request.</div>
              </div>
            </div>
          </div>
          <DialogHeader className="hidden"><DialogTitle>Form</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 py-4">
            <F label="Client (from CRM) *" full>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="tr-form-client"><SelectValue placeholder="Pick a client…" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_name} · {c.u_dise || ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Trainer (optional)">
              <Select value={form.trainer_id || "__upcoming__"} onValueChange={(v) => setForm({ ...form, trainer_id: v === "__upcoming__" ? "" : v })}>
                <SelectTrigger data-testid="tr-form-trainer"><SelectValue placeholder="Leave blank for Upcoming…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__upcoming__">— Leave Upcoming (no trainer) —</SelectItem>
                  {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.status}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Training Type *">
              <Select value={form.training_type} onValueChange={(v) => setForm({ ...form, training_type: v })}>
                <SelectTrigger data-testid="tr-form-type"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{TRAINING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </F>
            {form.training_type === "Other" && (
              <F label="Name the Training Type *" full>
                <Input value={form.type_other_name} onChange={(e) => setForm({ ...form, type_other_name: e.target.value })} data-testid="tr-form-type-other" />
              </F>
            )}
            <F label="From *"><Input type="date" value={form.training_from} onChange={(e) => setForm({ ...form, training_from: e.target.value })} data-testid="tr-form-from" /></F>
            <F label="To *"><Input type="date" value={form.training_to} onChange={(e) => setForm({ ...form, training_to: e.target.value })} data-testid="tr-form-to" /></F>
            <F label="Contact Name (override)"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} placeholder="Defaults to client's primary contact" data-testid="tr-form-contact-name" /></F>
            <F label="Contact Phone (override)"><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="tr-form-contact-phone" /></F>
            <F label="Remark" full><Textarea rows={2} value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="tr-form-remark" /></F>
          </div>
          <DialogFooter className="px-6 pb-5">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="tr-form-submit" data-keep-color>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Detail Drawer */}
      <Dialog open={!!activeAssignment} onOpenChange={(o) => !o && setActiveAssignment(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
          <div className="relative bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
            <div className="relative flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center flex-shrink-0">
                <GraduationCap size={20} className="text-indigo-700 dark:text-indigo-300" />
              </div>
              <div className="min-w-0 flex-1">
                <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 flex-wrap">
                  <span className="truncate">{activeAssignment?.client_name}</span>
                  <Badge className={`${STATUS_COLOR[activeAssignment?.status] || "bg-slate-100 text-slate-700"} text-[10px] font-semibold`} data-keep-color>{activeAssignment?.status}</Badge>
                </DialogTitle>
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">Training Assignment Details</div>
              </div>
            </div>
          </div>
          <DialogHeader className="hidden"><DialogTitle>Detail</DialogTitle></DialogHeader>
          {activeAssignment && (
            <div className="space-y-3 px-6 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <Info icon={<Building2 size={13} />} label="Client Address" value={activeAssignment.client_address} />
              <Info icon={<User size={13} />} label="Report To" value={`${activeAssignment.contact_name || "—"}${activeAssignment.contact_phone ? ` · ${activeAssignment.contact_phone}` : ""}`} />
              {activeAssignment.contact_email && <Info icon={<MapPin size={13} />} label="Contact Email" value={activeAssignment.contact_email} />}
              <Info icon={<Calendar size={13} />} label="Training Window" value={`${activeAssignment.training_from} → ${activeAssignment.training_to}`} />
              {activeAssignment.location_map_url && (
                <Info icon={<MapPin size={13} />} label="Location Map" value={<a href={activeAssignment.location_map_url} target="_blank" rel="noreferrer" className="text-indigo-700 dark:text-indigo-300 hover:underline" data-testid="tr-detail-map">Open on Google Maps →</a>} />
              )}
              </div>

              {/* v109 — Sales Associate reveal (visible for Assigned/In-Progress/Completed) */}
              {activeSalesAssoc && activeSalesAssoc.name && (
                <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3 py-2.5" data-testid="tr-detail-sales-assoc">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 mb-1 flex items-center gap-1.5">
                    <UserCog size={11} /> Sales Associate
                  </div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100 text-[13px]">{activeSalesAssoc.name}</div>
                  <div className="text-[11.5px] mono text-slate-700 dark:text-slate-200 mt-0.5">
                    {activeSalesAssoc.phone || "No company phone"} · {activeSalesAssoc.email || "No company email"}
                  </div>
                </div>
              )}
              <DateWorkflowPanel
                a={activeAssignment}
                userId={user?.id}
                isAdmin={isAdmin}
                isTrainer={isTrainer}
                isMaster={user?.role === "master_admin"}
                onConfirm={() => confirmDates(activeAssignment)}
                onEditDates={() => openEditDates(activeAssignment, "edit")}
                onRequestChange={() => openEditDates(activeAssignment, "request")}
                onDecide={decideDateRequest}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <Info icon={<GraduationCap size={13} />} label="Type" value={activeAssignment.training_type === "Other" ? (activeAssignment.type_other_name || "Other") : activeAssignment.training_type} />
              <Info icon={<User size={13} />} label="Assigned Trainer" value={activeAssignment.trainer_name || <span className="text-slate-400 italic">Unassigned</span>} />
              <Info icon={<User size={13} />} label="Created By" value={`${activeAssignment.created_by_name || "—"}${activeAssignment.created_by_role ? ` · ${activeAssignment.created_by_role}` : ""}`} />
              {activeAssignment.remark && <Info icon={<MapPin size={13} />} label="Remark" value={activeAssignment.remark} />}
              </div>

              {/* Pending requests (Upcoming only) */}
              {activeAssignment.status === "Upcoming" && (
                <div className="border-t border-slate-200 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">Assignment Requests</div>
                    {isTrainer && !(activeAssignment.pending_requests || []).some((r) => r.trainer_id === user?.id) && (
                      <Button size="sm" onClick={() => requestTraining(activeAssignment)} className="h-7 text-[11px] bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="tr-detail-request">
                        Request This Training
                      </Button>
                    )}
                  </div>
                  {(activeAssignment.pending_requests || []).length === 0 ? (
                    <div className="text-xs text-slate-400 italic">No trainer has requested this training yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {(activeAssignment.pending_requests || []).map((r) => (
                        <div key={r.trainer_id} className="border border-slate-200 rounded-md p-2.5 flex items-center justify-between gap-2" data-testid={`tr-pending-${r.trainer_id}`}>
                          <div className="min-w-0">
                            <div className="font-semibold text-slate-900 text-sm">{r.trainer_name}</div>
                            {r.note && <div className="text-[11px] text-slate-600 mt-0.5">{r.note}</div>}
                            <div className="text-[10px] mono text-slate-400">{r.requested_at ? new Date(r.requested_at).toLocaleString() : ""}</div>
                          </div>
                          {isAdmin && (
                            <div className="flex items-center gap-1.5">
                              <Button size="sm" onClick={() => approveRequest(activeAssignment, r)} className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white" data-testid={`tr-approve-${r.trainer_id}`}>
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => rejectRequest(activeAssignment, r)} className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" data-testid={`tr-reject-${r.trainer_id}`}>
                                Reject
                              </Button>
                            </div>
                          )}
                          {isTrainer && r.trainer_id === user?.id && (
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[10px]">Your request</Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin: direct-assign any trainer (bypass request flow) */}
                  {isAdmin && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Or assign a different trainer directly</div>
                      <Select onValueChange={(v) => directAssign(activeAssignment, v)}>
                        <SelectTrigger className="h-9" data-testid="tr-direct-assign"><SelectValue placeholder="Pick any trainer to assign…" /></SelectTrigger>
                        <SelectContent>
                          {trainers.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.status}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              )}

              {/* Travel dates — editable by trainer / admin */}
              <div className="border-t border-slate-200 pt-3">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-2">
                  <Plane size={12} /> Travel Dates
                </div>
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {(activeAssignment.travel_dates || []).length === 0 && <span className="text-xs text-slate-400 italic">No travel dates marked yet.</span>}
                  {(activeAssignment.travel_dates || []).map((d) => (
                    <Badge key={d} className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100 text-[10px] cursor-pointer" onClick={() => (isTrainer || isAdmin) && removeTravelDate(d)} data-testid={`tr-travel-${d}`}>
                      {d} {(isTrainer || isAdmin) && "×"}
                    </Badge>
                  ))}
                </div>
                {(isTrainer && activeAssignment.trainer_id === user?.id) || isAdmin ? (
                  <div className="flex gap-2">
                    <Input type="date" value={travelInput} onChange={(e) => setTravelInput(e.target.value)} className="h-9 w-44" data-testid="tr-travel-input" />
                    <Button size="sm" onClick={addTravelDate} className="h-9 bg-slate-900 hover:bg-slate-800 text-white" data-testid="tr-travel-add">+ Mark</Button>
                  </div>
                ) : null}
              </div>

              {((isTrainer && activeAssignment.trainer_id === user?.id) || isAdmin) && activeAssignment.status !== "Completed" && (
                <div className="border-t border-slate-200 pt-3 flex items-center gap-2 flex-wrap">
                  {/* v108 — Arrival Check-in surface inside the detail dialog */}
                  {activeAssignment.status === "Assigned" && !activeAssignment.arrival_check && isTrainer && (
                    <Button size="sm" onClick={() => setArrivalQueue([activeAssignment, ...(arrivalQueue.filter((x) => x.id !== activeAssignment.id))])} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="tr-open-arrival">
                      <MapPin size={13} className="mr-1.5" /> Mark Arrival at School
                    </Button>
                  )}
                  {activeAssignment.status === "Assigned" && !isTrainer && (
                    <Button size="sm" onClick={() => updateStatus("In Progress")} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tr-mark-progress">Mark In Progress</Button>
                  )}
                  {/* v109.9.165 — Apply Advance from an assigned training */}
                  {isTrainer && activeAssignment.trainer_id === user?.id && ["Assigned","In Progress"].includes(activeAssignment.status) && (
                    <Button
                      size="sm"
                      onClick={() => setApplyAdvanceFor(activeAssignment)}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      data-testid="tr-apply-advance"
                      title="Raise a Travel Advance for this training"
                    >
                      <Send size={13} className="mr-1.5" /> Apply Advance
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setCompleteDialog(activeAssignment)} className="bg-green-600 hover:bg-green-700 text-white" data-testid="tr-mark-completed">
                    <CheckCircle2 size={13} className="mr-1.5" /> Mark Completed (Upload Artefacts)
                  </Button>
                </div>
              )}

              {/* v108 — Read-only arrival log for everyone with access */}
              {activeAssignment.arrival_check && (
                <div className="border-t border-slate-200 pt-3" data-testid="tr-arrival-log">
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 mb-1.5">Arrival Check-in</div>
                  {activeAssignment.arrival_check.reached ? (
                    <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3 py-2 text-[12px]">
                      <div className="font-semibold text-emerald-700 dark:text-emerald-300">✓ Reached the school</div>
                      <div className="mono text-[11px] text-slate-700 dark:text-slate-200 mt-0.5">
                        Left home at {activeAssignment.arrival_check.left_home_at || "—"} · Arrived at {activeAssignment.arrival_check.arrived_at || "—"}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-md border border-rose-200 dark:border-rose-500/30 bg-rose-50/60 dark:bg-rose-500/10 px-3 py-2 text-[12px]">
                      <div className="font-semibold text-rose-700 dark:text-rose-300">✗ Could not reach the school</div>
                      <div className="text-[11px] text-slate-700 dark:text-slate-200 mt-0.5">Reason: {activeAssignment.arrival_check.reason_not_reached || "—"}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="px-6 pb-5">
            <Button variant="outline" onClick={() => setActiveAssignment(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Edit / Request dialog (mandatory remark) */}
      <Dialog open={!!dateEditDialog} onOpenChange={(o) => !o && setDateEditDialog(null)}>
        <DialogContent className="max-w-md" data-testid="tr-date-edit-dialog">
          <DialogHeader>
            <DialogTitle className="heading">
              {dateEditDialog?.mode === "request" ? "Request a Date Change" : "Edit Training Dates"}
            </DialogTitle>
          </DialogHeader>
          {dateEditDialog && (
            <div className="space-y-3 py-2">
              {dateEditDialog.mode === "request" && (
                <div className="text-[12px] bg-amber-50 border border-amber-200 text-amber-900 rounded-md px-3 py-2">
                  You&apos;ve used your one allowed edit. This request will be reviewed by Master Admin, who may approve as-is, override the dates, or reject.
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">New From *</Label>
                  <Input type="date" value={dateEditDialog.training_from}
                    onChange={(e) => setDateEditDialog({ ...dateEditDialog, training_from: e.target.value })}
                    className="mt-1.5 h-10" data-testid="tr-date-edit-from" />
                </div>
                <div>
                  <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">New To *</Label>
                  <Input type="date" value={dateEditDialog.training_to}
                    onChange={(e) => setDateEditDialog({ ...dateEditDialog, training_to: e.target.value })}
                    className="mt-1.5 h-10" data-testid="tr-date-edit-to" />
                </div>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">
                  {dateEditDialog.mode === "request" ? "Reason *" : "Remark *"}
                </Label>
                <textarea
                  value={dateEditDialog.remark}
                  onChange={(e) => setDateEditDialog({ ...dateEditDialog, remark: e.target.value })}
                  rows={3}
                  placeholder="Explain why these dates need to change…"
                  className="mt-1.5 w-full text-sm px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-md bg-white dark:bg-slate-900"
                  data-testid="tr-date-edit-remark"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDateEditDialog(null)}>Cancel</Button>
            <Button onClick={submitEditDates} className="bg-[var(--pk-accent)] hover:bg-[var(--pk-accent-hover)] text-white" data-testid="tr-date-edit-submit">
              {dateEditDialog?.mode === "request" ? "Send Request" : "Save Dates"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v107 — Raise Training Request Dialog */}
      <TrainingRequestDialog
        open={reqOpen}
        onClose={() => setReqOpen(false)}
        form={reqForm}
        setForm={setReqForm}
        trainers={trainers}
        activeEmployees={activeEmployees}
        onSubmit={submitTrainingRequest}
      />

      {/* v107 — Trainer: request-this-training details + remark dialog (replaces the old window.prompt) */}
      <Dialog open={!!requestingRow} onOpenChange={(o) => !o && setRequestingRow(null)}>
        <DialogContent className="max-w-md" data-testid="tr-request-dialog">
          <DialogHeader>
            <DialogTitle className="heading flex items-center gap-2">
              <GraduationCap size={16} className="text-indigo-700 dark:text-indigo-300" /> Request Training
            </DialogTitle>
            <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">Ask to be assigned to this upcoming training. Admin will approve/reject.</div>
          </DialogHeader>
          {requestingRow && (
            <div className="space-y-3 py-2">
              <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2.5 text-[12.5px] space-y-1">
                <div className="font-bold text-slate-900 dark:text-slate-100">{requestingRow.client_name}</div>
                <div className="text-[11px] text-slate-600 dark:text-slate-300">{requestingRow.client_address}</div>
                <div className="mono text-[11px] text-slate-700 dark:text-slate-200">
                  {requestingRow.training_type === "Other" ? (requestingRow.type_other_name || "Other") : requestingRow.training_type} · {requestingRow.training_from} → {requestingRow.training_to}
                </div>
                {(requestingRow.contact_name || requestingRow.contact_phone) && (
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">Report to: {requestingRow.contact_name || "—"}{requestingRow.contact_phone ? ` · ${requestingRow.contact_phone}` : ""}</div>
                )}
                {requestingRow.remark && <div className="text-[11px] italic text-slate-500 dark:text-slate-400">&ldquo;{requestingRow.remark}&rdquo;</div>}
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">Your Remark (optional)</Label>
                <Textarea
                  rows={3}
                  value={requestingNote}
                  onChange={(e) => setRequestingNote(e.target.value)}
                  placeholder="Anything the admin should know before assigning you (availability, travel, past experience with this school, etc.)"
                  className="mt-1.5"
                  data-testid="tr-request-note"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestingRow(null)}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!requestingRow) return;
                await requestTraining(requestingRow, requestingNote.trim());
                setRequestingRow(null);
                setRequestingNote("");
              }}
              className="!bg-indigo-600 hover:!bg-indigo-700 !text-white"
              data-testid="tr-request-submit"
              data-keep-color
            >
              <Send size={13} className="mr-1.5" /> Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* v109.9.40 — TabAccessChooser removed per user request */}

      {/* v108 — Arrival check auto-modal (trainer only, one at a time) */}
      {isTrainer && arrivalQueue.length > 0 && (
        <ArrivalCheckDialog
          assignment={arrivalQueue[0]}
          onClose={() => setArrivalQueue((q) => q.slice(1))}
          onSaved={() => {
            setArrivalQueue((q) => q.slice(1));
            load();
          }}
        />
      )}

      {/* v109.9.165 — Apply Advance for a specific training assignment.
          Pre-fills the Travel Advance form with the training's client,
          purpose, and date window. */}
      {applyAdvanceFor && (
        <TravelAdvanceFormDialog
          open
          onClose={() => setApplyAdvanceFor(null)}
          onCreated={() => setApplyAdvanceFor(null)}
          prefill={{
            client_ids: applyAdvanceFor.client_id ? [applyAdvanceFor.client_id] : [],
            client_names: applyAdvanceFor.client_name ? [applyAdvanceFor.client_name] : [],
            purpose_of_travel: `Training at ${applyAdvanceFor.client_name || applyAdvanceFor.title || ""}`.trim(),
            destination: [applyAdvanceFor.location || "", applyAdvanceFor.client_name || ""].filter(Boolean).join(" · "),
            start_date: (applyAdvanceFor.start_datetime || "").slice(0, 10),
            end_date: (applyAdvanceFor.end_datetime || applyAdvanceFor.start_datetime || "").slice(0, 10),
            trainer_assignment_id: applyAdvanceFor.id,
          }}
        />
      )}

      {/* v108 — Mark Complete → upload artefacts dialog */}
      {completeDialog && (
        <CompletionUploadDialog
          assignment={completeDialog}
          onClose={() => setCompleteDialog(null)}
          onSaved={() => {
            setCompleteDialog(null);
            setActiveAssignment(null);
            load();
          }}
        />
      )}
      {/* v109.9.9 — Save-and-request dialog (auto-opens on 403) */}
      {trReqDialog}
    </div>
  );
}

export function emptyReqForm() {
  return {
    client_id: "",
    client_name: "",
    client_types: [],
    client_placeholder: false,
    product_topic: "",
    preferred_trainer_id: "",
    preferred_trainer_name: "",
    // v107 Part 10 — explicit start/end date+time
    start_datetime: "",
    end_datetime: "",
    duration: "",
    mode: "physical",
    location: "",
    location_map_url: "",
    sales_associate_id: "",
    sales_associate_name: "",
    assigned_by_id: "",
    assigned_by_name: "",
    // Report-To: pick from existing CRM contact list OR provide new (name+phone)
    report_to_name: "",
    report_to_phone: "",
    report_to_designation: "",
    report_to_is_new: false,
    notes: "",
    source: "trainers",
    source_ref: "",
  };
}

// (Legacy per-module hook removed — Trainers now uses the top-bar global toggle.)

/* ==== TAB 1 · Trainer roster helpers ==== */
function TrainerCardsGrid({ trainers, statusMap }) {
  if (trainers.length === 0) return <div className="text-center py-12 text-slate-400 text-sm">No trainers yet.</div>;
  // v107 Part 11 — compute per-trainer rank from live status feed
  const ranked = [...trainers].map((t) => ({
    id: t.id,
    score: ((statusMap[t.id]?.approved_projects || 0) + (statusMap[t.id]?.training_days || 0)),
    stars: (statusMap[t.id]?.stars_earned || 0),
  })).sort((a, b) => b.score - a.score || b.stars - a.stars);
  const rankMap = {};
  ranked.forEach((r, i) => { rankMap[r.id] = i + 1; });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {trainers.map((t) => {
        const live = statusMap[t.id];
        const attStatus = live?.attendance_status || "Not Punched In";
        const workStatus = live?.work_status || "In Office";
        const upcLeaves = live?.upcoming_leaves || [];
        return (
          <div key={t.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 transition-all duration-200 hover:shadow-sm hover:border-indigo-300 dark:hover:border-indigo-500/40" data-testid={`tr-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-10 h-10 rounded-md bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-200 dark:ring-indigo-500/30 flex items-center justify-center flex-shrink-0">
                  <GraduationCap size={18} strokeWidth={2} className="text-indigo-700 dark:text-indigo-300" />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 dark:text-slate-100 truncate text-[14px]">{t.name}</div>
                  <div className="text-[10px] text-slate-500 mono truncate">{t.email}</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-keep-color>{attStatus}</Badge>
                <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-keep-color>{workStatus}</Badge>
              </div>
            </div>
            {t.current_assignment ? (
              <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 mt-2 space-y-1 text-[12px] text-slate-700 dark:text-slate-200">
                <div className="flex items-center gap-1.5"><Building2 size={11} className="text-slate-400" /> <span className="font-semibold">{t.current_assignment.client_name}</span></div>
                <div className="flex items-center gap-1.5"><Calendar size={11} className="text-slate-400" /> {t.current_assignment.training_from} → {t.current_assignment.training_to}</div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">Type: {t.current_assignment.training_type === "Other" ? (t.current_assignment.type_other_name || "Other") : t.current_assignment.training_type}</div>
              </div>
            ) : (
              <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 mt-2 text-[12px] text-slate-400 italic">No active assignment.</div>
            )}
            {/* v107 Part 11 — Performance surface (Rank / Rating / Approved / Training Days) */}
            <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 mt-2 grid grid-cols-4 gap-2 text-center" data-testid={`tr-perf-${t.id}`}>
              <MiniStat label="Approved" value={live?.approved_projects ?? 0} />
              <MiniStat label="Days" value={live?.training_days ?? 0} />
              <MiniStat label="Stars" value={live?.max_possible_stars ? `${live.stars_earned}/${live.max_possible_stars}` : "—"} />
              <MiniStat label="Rank" value={rankMap[t.id] ? `#${rankMap[t.id]}` : "—"} />
            </div>
            {/* v107 — Upcoming leaves (Submitted OR Approved, next 90 days) */}
            <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 mt-2">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 dark:text-slate-400 mb-1">Upcoming Leaves (90d)</div>
              {upcLeaves.length === 0 ? (
                <span className="text-[10px] text-emerald-700 bg-emerald-50 dark:bg-emerald-500/10 dark:text-emerald-300 ring-1 ring-emerald-200 dark:ring-emerald-500/30 rounded px-1.5 py-0.5" data-testid={`tr-no-leaves-${t.id}`}>No leave planned</span>
              ) : (
                <div className="flex flex-wrap gap-1" data-testid={`tr-leaves-${t.id}`}>
                  {upcLeaves.slice(0, 4).map((l, i) => {
                    const isApproved = l.status === "Approved";
                    return (
                      <span key={i} className={`text-[10px] rounded px-1.5 py-0.5 mono ring-1 ${isApproved ? "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30" : "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/30"}`}>
                        <b>{isApproved ? "✓" : "⏳"}</b> {l.leave_type} · {fmtDateStr(l.from_date)}→{fmtDateStr(l.to_date)}
                      </span>
                    );
                  })}
                  {upcLeaves.length > 4 && <span className="text-[10px] text-slate-500">+{upcLeaves.length - 4} more</span>}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TrainersListTable({ trainers, statusMap }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <tr>
            <Th>Name</Th><Th>Email</Th><Th>Current Assignment</Th><Th>Type</Th><Th>Window</Th><Th>Upcoming Leaves</Th>
          </tr>
        </thead>
        <tbody>
          {trainers.length === 0 && <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">No trainers yet.</td></tr>}
          {trainers.map((t) => {
            const upcLeaves = (statusMap || {})[t.id]?.upcoming_leaves || [];
            return (
              <tr key={t.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`tr-row-${t.id}`}>
                <td className="px-4 py-3 font-bold text-slate-900 dark:text-slate-100" data-label="Name">{t.name}</td>
                <td className="px-4 py-3 mono text-[11px] text-slate-600 dark:text-slate-300" data-label="Email">{t.email}</td>
                <td className="px-4 py-3 text-[12px]" data-label="Current Assignment">{t.current_assignment?.client_name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                <td className="px-4 py-3 text-[12px]" data-label="Type">{t.current_assignment ? (t.current_assignment.training_type === "Other" ? (t.current_assignment.type_other_name || "Other") : t.current_assignment.training_type) : "—"}</td>
                <td className="px-4 py-3 mono text-[11px]" data-label="Window">{t.current_assignment ? `${t.current_assignment.training_from} → ${t.current_assignment.training_to}` : "—"}</td>
                <td className="px-4 py-3" data-label="Upcoming Leaves">
                  {upcLeaves.length === 0 ? (
                    <span className="text-[10px] text-emerald-700 dark:text-emerald-300">—</span>
                  ) : (
                    <div className="flex flex-wrap gap-1 max-w-[280px]" data-testid={`tr-list-leaves-${t.id}`}>
                      {upcLeaves.slice(0, 3).map((l, i) => (
                        <span key={i} className={`text-[10px] rounded px-1.5 py-0.5 mono ring-1 ${l.status === "Approved" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-800 ring-amber-200"}`}>
                          {l.status === "Approved" ? "✓" : "⏳"} {l.leave_type} · {fmtDateStr(l.from_date)}→{fmtDateStr(l.to_date)}
                        </span>
                      ))}
                      {upcLeaves.length > 3 && <span className="text-[10px] text-slate-500">+{upcLeaves.length - 3}</span>}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ==== TAB 2 · Upcoming Table ==== */
function UpcomingTable({ rows, isAdmin, isTrainer, user, onOpen, onEdit, onDelete, onRequest }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-x-auto">
      <table className="w-full text-sm responsive-table">
        <thead className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <tr>
            <Th>Client</Th><Th>Type</Th><Th>Window</Th><Th>Created By</Th><Th>Requests</Th>
            <Th className="text-right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={6} className="text-center py-12 text-slate-400 text-sm">
              {isAdmin ? "No upcoming trainings. Click 'New Training' to add one." : "No upcoming trainings to request right now."}
            </td></tr>
          )}
          {rows.map((a) => {
            const requests = a.pending_requests || [];
            const myRequest = isTrainer && requests.find((r) => r.trainer_id === user?.id);
            return (
              <tr key={a.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`tr-upcoming-${a.id}`}>
                <td className="px-4 py-3" data-label="Client">
                  <div className="font-bold text-slate-900 dark:text-slate-100">{a.client_name}</div>
                  <div className="text-[10px] text-slate-500 truncate max-w-[260px]">{a.client_address}</div>
                </td>
                <td className="px-4 py-3 text-[12px]" data-label="Type">{a.training_type === "Other" ? (a.type_other_name || "Other") : a.training_type}</td>
                <td className="px-4 py-3 mono text-[11px]" data-label="Window">{a.training_from} → {a.training_to}</td>
                <td className="px-4 py-3 text-[11px]" data-label="Created By">
                  <div className="font-medium text-slate-800 dark:text-slate-100">{a.created_by_name}</div>
                  <div className="text-[10px] text-slate-500">{a.created_by_role || ""}</div>
                </td>
                <td className="px-4 py-3" data-label="Requests">
                  {requests.length === 0 ? (
                    <span className="text-[11px] text-slate-400 italic">No requests</span>
                  ) : (
                    <Badge className="bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 text-[10px]" data-keep-color>{requests.length} pending</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right" data-label="Action">
                  <div className="flex items-center justify-end gap-2">
                    {/* v107 — Trainer role gets a single "Request Training" button that opens the details+remark dialog. */}
                    {isTrainer ? (
                      myRequest ? (
                        <Badge className="bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[10px]" data-testid={`tr-requested-${a.id}`}>You requested</Badge>
                      ) : (
                        <Button size="sm" onClick={() => onRequest(a)} className="h-8 text-xs !bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid={`tr-request-${a.id}`} data-keep-color>Request Training</Button>
                      )
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => onOpen(a)} className="h-8 text-xs" data-testid={`tr-open-upcoming-${a.id}`}>Open</Button>
                    )}
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => onEdit(a)} className="h-8 text-xs" data-testid={`tr-edit-upcoming-${a.id}`}>Edit</Button>}
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => onDelete(a)} className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`tr-delete-${a.id}`}>Remove</Button>}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ==== TAB 4 · Approval Queue ==== */

// v107 Part 10 — Icon-only action button (Green / Red / Blue / Orange)
function IconBtn({ onClick, tone, title, testid, children }) {
  const toneMap = {
    green: "text-emerald-600 border-emerald-200 hover:bg-emerald-50 dark:text-emerald-400 dark:border-emerald-500/30 dark:hover:bg-emerald-500/10",
    red: "text-rose-600 border-rose-200 hover:bg-rose-50 dark:text-rose-400 dark:border-rose-500/30 dark:hover:bg-rose-500/10",
    blue: "text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-500/30 dark:hover:bg-blue-500/10",
    orange: "text-orange-600 border-orange-200 hover:bg-orange-50 dark:text-orange-400 dark:border-orange-500/30 dark:hover:bg-orange-500/10",
  };
  return (
    <Button onClick={onClick} size="sm" variant="outline" title={title} data-testid={testid}
      className={`h-8 w-8 p-0 shrink-0 border ${toneMap[tone] || ""}`}>
      {children}
    </Button>
  );
}

function ApprovalQueue({ canApproval, pendingReqs, myReqs, onDecide, onReload, activeEmployees, trainers }) {
  const rows = canApproval ? pendingReqs : myReqs;
  const title = canApproval ? "Pending Training Requests" : "My Training Requests";
  const [detailsFor, setDetailsFor] = useState(null);
  const [details, setDetails] = useState(null);
  // v108 — Full editable review dialog
  const [reviewReq, setReviewReq] = useState(null);
  // v109 — Card / List view synced with top-bar toggle
  const [approvalVm] = useGlobalViewMode();
  const openDetails = async (r) => {
    setDetailsFor(r);
    setDetails(null);
    try {
      const { data } = await api.get(`/training-requests/${r.id}/client-details`);
      setDetails(data);
    } catch { setDetails({ error: true }); }
  };
  return (
    <div className="space-y-3">
      {!canApproval && (
        <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-[12px] text-slate-700 dark:text-slate-200">
          You&apos;ll see your own training requests here. If no approver takes action within 24h, the request is auto-approved.
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">{title}</div>
        <Badge className="bg-slate-100 text-slate-700 text-[10px]" data-keep-color>{rows.length}</Badge>
      </div>
      {rows.length === 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-4 py-8 text-center text-slate-400 text-[12.5px] italic">Nothing pending.</div>
      )}

      {rows.length > 0 && approvalVm === "card" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex flex-col gap-2.5 transition-colors hover:border-indigo-300 dark:hover:border-indigo-500/40" data-testid={`tr-req-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{r.client_name || r.title || "—"}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">by {r.created_by_name} · {new Date(r.created_at).toLocaleString("en-IN")}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <Badge className={`text-[10px] font-semibold ${r.status === "pending" ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : r.status === "approved" || r.status === "assigned" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : r.status === "rejected" ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200" : "bg-slate-100 text-slate-700"}`} data-keep-color>{r.status}</Badge>
                  {r.is_auto_approved && <Badge className="bg-violet-50 text-violet-700 ring-1 ring-violet-200 text-[9px] font-semibold" data-keep-color>Auto-approved</Badge>}
                </div>
              </div>
              {(r.client_types || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(r.client_types || []).map((t) => <span key={t} className="text-[10px] font-semibold uppercase tracking-wider text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 ring-1 ring-indigo-200 dark:ring-indigo-500/30 rounded px-1.5 py-0.5">{t}</span>)}
                </div>
              )}
              <div className="text-[11.5px] text-slate-700 dark:text-slate-200 space-y-1">
                {r.start_datetime && <div className="mono text-[11px] text-slate-600 dark:text-slate-300">{new Date(r.start_datetime).toLocaleString("en-IN")} → {r.end_datetime ? new Date(r.end_datetime).toLocaleString("en-IN") : ""}</div>}
                {r.sales_associate_name && <div className="text-[11px]"><span className="text-slate-500">SA:</span> <span className="font-semibold">{r.sales_associate_name}</span></div>}
                {r.assigned_by_name && <div className="text-[11px]"><span className="text-slate-500">Assigned by:</span> <span className="font-semibold">{r.assigned_by_name}</span></div>}
                {r.report_to_name && <div className="text-[11px]"><span className="text-slate-500">Report To:</span> {r.report_to_name}{r.report_to_phone ? ` · ${r.report_to_phone}` : ""}</div>}
              </div>
              {r.notes && <div className="text-[11px] text-slate-500 italic line-clamp-2 border-t border-slate-100 dark:border-slate-800 pt-2">&ldquo;{r.notes}&rdquo;</div>}
              <div className="flex items-center justify-end gap-1.5 flex-wrap pt-2 border-t border-slate-100 dark:border-slate-800">
                <Button size="sm" variant="outline" onClick={() => setReviewReq(r)} className="h-8 text-xs" data-testid={`tr-req-open-card-${r.id}`}>Open</Button>
                {canApproval && r.status === "pending" && (
                  <>
                    <IconBtn onClick={() => onDecide(r, "approve")} tone="green" title="Approve" testid={`tr-req-approve-card-${r.id}`}><CheckCircle2 size={13} /></IconBtn>
                    <IconBtn onClick={() => onDecide(r, "reject")} tone="red" title="Reject" testid={`tr-req-reject-card-${r.id}`}><XCircle size={13} /></IconBtn>
                  </>
                )}
                {(r.status === "approved" || r.status === "assigned") && r.client_id && (
                  <button type="button" onClick={() => openDetails(r)} className="text-[11px] text-indigo-700 dark:text-indigo-300 hover:underline ml-1" data-testid={`tr-req-details-card-${r.id}`}>
                    Contacts →
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {rows.length > 0 && approvalVm !== "card" && (
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((r) => (
            <li key={r.id} className="px-4 py-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`tr-req-row-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 dark:text-slate-100 text-[13px] flex items-center gap-2 flex-wrap">
                  {r.client_name || r.title || "—"}
                  {(r.client_types || []).length > 0 && <Badge className="bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 text-[9px] font-semibold" data-keep-color>{(r.client_types || []).join(", ")}</Badge>}
                  {r.is_auto_approved && <Badge className="bg-violet-50 text-violet-700 ring-1 ring-violet-200 text-[9px] font-semibold" data-keep-color>Auto-approved</Badge>}
                </div>
                <div className="text-[11.5px] text-slate-600 dark:text-slate-300 mt-0.5">
                  {r.start_datetime ? new Date(r.start_datetime).toLocaleString("en-IN") : ""}
                  {r.end_datetime ? ` → ${new Date(r.end_datetime).toLocaleString("en-IN")}` : ""}
                  {r.mode ? ` · ${r.mode}` : ""}
                  {r.sales_associate_name ? ` · SA: ${r.sales_associate_name}` : ""}
                  {r.assigned_by_name ? ` · Assigned by: ${r.assigned_by_name}` : ""}
                </div>
                {r.report_to_name && (
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Report To: {r.report_to_name}{r.report_to_phone ? ` · ${r.report_to_phone}` : ""}</div>
                )}
                {r.notes && <div className="text-[11px] text-slate-500 italic mt-1 line-clamp-2">&ldquo;{r.notes}&rdquo;</div>}
                <div className="text-[10px] text-slate-400 mt-1">
                  by {r.created_by_name}{r.source && r.source !== "trainers" ? ` · via ${r.source}` : ""} · {new Date(r.created_at).toLocaleString("en-IN")}
                </div>
                {(r.status === "approved" || r.status === "assigned") && r.client_id && (
                  <button type="button" onClick={() => openDetails(r)} className="mt-1.5 text-[11px] text-indigo-700 dark:text-indigo-300 hover:underline" data-testid={`tr-req-details-${r.id}`}>
                    View client + sales exec details →
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                {r.status !== "pending" && !r.is_auto_approved && (
                  <Badge className={`text-[10px] font-semibold ${r.status === "approved" || r.status === "assigned" ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" : r.status === "rejected" ? "bg-rose-50 text-rose-700 ring-1 ring-rose-200" : "bg-slate-100 text-slate-700"}`} data-keep-color>{r.status}</Badge>
                )}
                <Button size="sm" variant="outline" onClick={() => setReviewReq(r)} className="h-8 text-xs" data-testid={`tr-req-open-${r.id}`}>Open</Button>
                {canApproval && r.status === "pending" && (
                  <>
                    <IconBtn onClick={() => onDecide(r, "approve")} tone="green" title="Approve" testid={`tr-req-approve-${r.id}`}><CheckCircle2 size={13} /></IconBtn>
                    <IconBtn onClick={() => onDecide(r, "reject")} tone="red" title="Reject" testid={`tr-req-reject-${r.id}`}><XCircle size={13} /></IconBtn>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
      )}

      {/* v108 — Full review + edit dialog */}
      <ApprovalReviewDialog
        req={reviewReq}
        onClose={() => setReviewReq(null)}
        canApproval={canApproval}
        activeEmployees={activeEmployees}
        trainers={trainers}
        onSaved={() => { setReviewReq(null); onReload && onReload(); }}
      />

      {/* v107 Part 10 — Client + Sales Executive details dialog */}
      <Dialog open={!!detailsFor} onOpenChange={(o) => !o && setDetailsFor(null)}>
        <DialogContent className="max-w-lg" data-testid="tr-req-details-dialog">
          <DialogHeader>
            <DialogTitle className="heading">{detailsFor?.title} — Contacts</DialogTitle>
          </DialogHeader>
          {details === null && <div className="py-6 text-center text-slate-400 text-[12.5px] italic">Loading…</div>}
          {details?.error && <div className="py-4 text-center text-rose-600 text-[12.5px]">Could not load details.</div>}
          {details && !details.error && (
            <div className="space-y-3 py-1">
              {details.client && (
                <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Client</div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100 text-[13px]">{details.client.client_name}</div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400">{[details.client.u_dise, details.client.city, details.client.state].filter(Boolean).join(" · ")}</div>
                  {details.client.address && <div className="text-[11px] text-slate-500 mt-1">{details.client.address}</div>}
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Client Contacts</div>
                {details.contacts.length === 0 ? (
                  <div className="text-[11.5px] italic text-slate-400 px-2">No contacts on file for this client.</div>
                ) : (
                  <ul className="divide-y divide-slate-100 dark:divide-slate-800 border border-slate-200 dark:border-slate-800 rounded-md">
                    {details.contacts.map((c, i) => (
                      <li key={i} className="px-3 py-2 text-[12px]" data-testid={`tr-req-contact-${i}`}>
                        <div className="font-semibold text-slate-800 dark:text-slate-100">{c.name || "—"} {c.designation && <span className="font-normal text-slate-500">· {c.designation}</span>}</div>
                        <div className="text-[11px] text-slate-600 dark:text-slate-300 mono">{c.phone || "—"}{c.email ? ` · ${c.email}` : ""}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {details.sales_executive && (
                <div className="rounded-md border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/60 dark:bg-emerald-500/10 px-3 py-2" data-testid="tr-req-sales-exec">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 dark:text-emerald-300 mb-1">Sales Executive</div>
                  <div className="font-semibold text-slate-900 dark:text-slate-100 text-[13px]">{details.sales_executive.name}</div>
                  <div className="text-[11px] mono text-slate-700 dark:text-slate-200">{details.sales_executive.phone || "—"}{details.sales_executive.email ? ` · ${details.sales_executive.email}` : ""}</div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==== Raise Training Request Dialog ==== */
export function TrainingRequestDialog({ open, onClose, form, setForm, trainers, activeEmployees, onSubmit, mode = "create", reviewMeta = null, footerExtra = null }) {
  // v109.9.165 — `mode="review"` renders the same form used for Raise a
  // Training Request but with a different header + footer so the
  // Approval-tab review dialog stays 1:1 with the request form (single
  // source of truth for fields).
  const isReview = mode === "review";
  const [clientQ, setClientQ] = useState("");
  const [clientResults, setClientResults] = useState([]);
  const [clientOpen, setClientOpen] = useState(false);
  // v108 — School contacts fetched once a client is picked
  const [clientContacts, setClientContacts] = useState([]);
  useEffect(() => {
    if (!open) return;
    setClientQ(form.client_name || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/training-requests/client-search", { params: { q: clientQ } });
        setClientResults(data || []);
      } catch { setClientResults([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [clientQ, open]);
  // v108 — After picking a client, fetch its full details (types, contacts, SA)
  useEffect(() => {
    if (!open || !form.client_id) { setClientContacts([]); return; }
    let alive = true;
    api.get(`/training-requests/client-full/${form.client_id}`)
      .then(({ data }) => {
        if (!alive) return;
        setClientContacts(data.contacts || []);
        // Auto-fill: client_types + sales associate (only if not already set)
        setForm((f) => ({
          ...f,
          client_types: data.client_types || [],
          sales_associate_id: data.sales_associate_id || "",
          sales_associate_name: data.sales_associate_name || "",
          product_topic: f.product_topic || (data.client_types && data.client_types[0]) || "",
        }));
      })
      .catch(() => setClientContacts([]));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.client_id, open]);
  if (!open) return null;
  const contactOptionValue = form.report_to_is_new
    ? "__new__"
    : (form.report_to_name && form.report_to_phone)
      ? `${form.report_to_name}||${form.report_to_phone}`
      : "";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="tr-req-dialog">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-white dark:bg-slate-950 ring-1 ring-slate-200 dark:ring-slate-800 flex items-center justify-center">
              {isReview ? <ShieldCheck size={18} className="text-indigo-700 dark:text-indigo-300" /> : <Send size={18} className="text-indigo-700 dark:text-indigo-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2 flex-wrap">
                {isReview ? (reviewMeta?.client_name || "Review Training Request") : "Raise Training Request"}
                {isReview && reviewMeta?.status && (
                  <Badge className={`text-[10px] font-semibold ${reviewMeta.status === "pending" ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : "bg-slate-100 text-slate-700"}`} data-keep-color>{reviewMeta.status}</Badge>
                )}
              </DialogTitle>
              <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5">
                {isReview
                  ? `Raised by ${reviewMeta?.created_by_name || ""}${reviewMeta?.created_at ? ` · ${new Date(reviewMeta.created_at).toLocaleString("en-IN")}` : ""}`
                  : "Master Admin (or Approval-tab users) will review. Auto-approved after 24h if no action is taken."}
              </div>
            </div>
          </div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Raise</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 px-6 py-4">
          {/* v108 — School / Client (with CRM autocomplete) */}
          {/* v109.9.160 — Bypass toggle: requester ticks "Master Admin will auto-fill" */}
          <F label="School / Client *" full>
            <label className="flex items-center gap-2 mb-1.5 select-none" data-testid="tr-req-placeholder-toggle-label">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                checked={!!form.client_placeholder}
                onChange={(e) => {
                  const on = e.target.checked;
                  setForm({
                    ...form,
                    client_placeholder: on,
                    client_id: on ? "" : form.client_id,
                    client_name: on ? "— Master Admin will auto-fill —" : "",
                    client_types: on ? [] : form.client_types,
                    sales_associate_id: on ? "" : form.sales_associate_id,
                    sales_associate_name: on ? "" : form.sales_associate_name,
                  });
                  setClientQ(on ? "" : "");
                  setClientOpen(false);
                }}
                data-testid="tr-req-placeholder-toggle"
              />
              <span className="text-[11.5px] text-slate-600 dark:text-slate-300">
                I don&apos;t know the School / Client — <span className="font-semibold text-indigo-700">Master Admin will auto-fill</span> on approval.
              </span>
            </label>
            <div className="relative">
              <Input
                value={form.client_placeholder ? "— Master Admin will auto-fill —" : clientQ}
                onChange={(e) => {
                  if (form.client_placeholder) return;
                  setClientQ(e.target.value);
                  setClientOpen(true);
                  setForm({ ...form, client_id: "", client_name: e.target.value, client_types: [], sales_associate_id: "", sales_associate_name: "" });
                }}
                onFocus={() => { if (!form.client_placeholder) setClientOpen(true); }}
                onBlur={() => setTimeout(() => setClientOpen(false), 180)}
                placeholder="Type a school / client name…"
                disabled={!!form.client_placeholder}
                className={form.client_placeholder ? "bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-200 italic" : ""}
                data-testid="tr-req-client-search"
              />
              {!form.client_placeholder && clientOpen && clientResults.length > 0 && (
                <div className="absolute z-20 mt-1 left-0 right-0 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 shadow-lg max-h-56 overflow-y-auto" data-testid="tr-req-client-list">
                  {clientResults.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onMouseDown={() => {
                        setForm({
                          ...form,
                          client_id: c.id,
                          client_name: c.client_name,
                          client_types: c.client_types || [],
                          sales_associate_id: c.sales_associate_id || "",
                          sales_associate_name: c.sales_associate_name || "",
                        });
                        setClientQ(c.client_name);
                        setClientOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 border-b border-slate-100 dark:border-slate-800 last:border-b-0"
                      data-testid={`tr-req-client-opt-${c.id}`}
                    >
                      <div className="text-[12.5px] font-semibold text-slate-800 dark:text-slate-100">{c.client_name}</div>
                      <div className="text-[10.5px] text-slate-500">
                        {[c.u_dise, c.city, c.state].filter(Boolean).join(" · ")}
                        {(c.client_types || []).length > 0 && <span className="ml-1">· {(c.client_types || []).join(", ")}</span>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {form.client_id && !form.client_placeholder && (
                <div className="mt-1 text-[10.5px] text-emerald-700 dark:text-emerald-300">✓ Linked to CRM · contacts will unlock after approval.</div>
              )}
              {form.client_placeholder && (
                <div className="mt-1 text-[10.5px] text-amber-700 dark:text-amber-300">Master Admin will pick the actual School / Client while approving this request.</div>
              )}
            </div>
          </F>

          {/* v108 — School Type (auto-fetched, read-only) */}
          <F label="School Type" full>
            <Input value={(form.client_types || []).join(", ") || ""} readOnly placeholder="Auto-fetched from CRM once a client is selected" className="bg-slate-50 dark:bg-slate-900" data-testid="tr-req-school-type" />
          </F>

          {/* v108 — Location (Google Maps link) */}
          <F label="Location · Google Maps link" full>
            <Input
              value={form.location_map_url}
              onChange={(e) => setForm({ ...form, location_map_url: e.target.value })}
              placeholder="Paste the school's Google Maps link (maps.google.com / goo.gl/maps)"
              data-testid="tr-req-map-url"
            />
          </F>

          {/* v108 — Sales Associate (auto-fetched, read-only) */}
          <F label="Sales Associate">
            <Input value={form.sales_associate_name || ""} readOnly placeholder="Auto-fetched from CRM" className="bg-slate-50 dark:bg-slate-900" data-testid="tr-req-sa" />
          </F>

          {/* v108 — Who assigned this training */}
          <F label="Who assigned this training *">
            <Select
              value={form.assigned_by_id || ""}
              onValueChange={(v) => {
                const emp = (activeEmployees || []).find((e) => e.id === v);
                setForm({ ...form, assigned_by_id: v, assigned_by_name: emp?.name || "" });
              }}
            >
              <SelectTrigger data-testid="tr-req-assigned-by"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
              <SelectContent className="max-h-64">
                {(activeEmployees || []).map((e) => (
                  <SelectItem key={e.id} value={e.id}>{e.name} · {e.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>

          <F label="Preferred Trainer (optional)">
            <Select value={form.preferred_trainer_id || "__none__"} onValueChange={(v) => {
              if (v === "__none__") { setForm({ ...form, preferred_trainer_id: "", preferred_trainer_name: "" }); return; }
              const t = (trainers || []).find((x) => x.id === v);
              setForm({ ...form, preferred_trainer_id: v, preferred_trainer_name: t?.name || "" });
            }}>
              <SelectTrigger data-testid="tr-req-trainer"><SelectValue placeholder="Any trainer" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Any trainer —</SelectItem>
                {(trainers || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>

          {/* Explicit start/end date+time */}
          <F label="Training Start (date + time) *"><Input type="datetime-local" value={form.start_datetime} onChange={(e) => setForm({ ...form, start_datetime: e.target.value })} data-testid="tr-req-start" /></F>
          <F label="Training End (date + time) *"><Input type="datetime-local" value={form.end_datetime} onChange={(e) => setForm({ ...form, end_datetime: e.target.value })} data-testid="tr-req-end" /></F>
          <F label="Duration (optional)"><Input value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g. 2 hrs, half-day" data-testid="tr-req-duration" /></F>
          <F label="Mode">
            <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
              <SelectTrigger data-testid="tr-req-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="physical">Physical / On-site</SelectItem>
                <SelectItem value="online">Online</SelectItem>
              </SelectContent>
            </Select>
          </F>

          {/* v108 — Report To (pick from CRM contact list OR write your own → CRM will remember it) */}
          <F label="Report To *" full>
            <div className="space-y-2">
              <Select
                value={contactOptionValue}
                onValueChange={(v) => {
                  if (v === "__new__") {
                    setForm({ ...form, report_to_is_new: true, report_to_name: "", report_to_phone: "", report_to_designation: "" });
                  } else {
                    const [n, p] = v.split("||");
                    const c = clientContacts.find((x) => x.name === n && (x.phone || "") === (p || ""));
                    setForm({
                      ...form,
                      report_to_is_new: false,
                      report_to_name: c?.name || n || "",
                      report_to_phone: c?.phone || p || "",
                      report_to_designation: c?.designation || "",
                    });
                  }
                }}
              >
                <SelectTrigger data-testid="tr-req-report-to-pick"><SelectValue placeholder={form.client_id ? "Pick a school contact — or add a new one" : "Select a school/client first…"} /></SelectTrigger>
                <SelectContent className="max-h-64">
                  {clientContacts.map((c, i) => (
                    <SelectItem key={i} value={`${c.name}||${c.phone || ""}`}>
                      {c.name || "—"}{c.designation ? ` · ${c.designation}` : ""}{c.phone ? ` · ${c.phone}` : ""}
                    </SelectItem>
                  ))}
                  <SelectItem value="__new__">+ Add a new Report-To contact…</SelectItem>
                </SelectContent>
              </Select>
              {form.report_to_is_new && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 rounded-md border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/40 dark:bg-indigo-500/5 p-2" data-testid="tr-req-new-contact">
                  <Input value={form.report_to_name} onChange={(e) => setForm({ ...form, report_to_name: e.target.value })} placeholder="Contact name" data-testid="tr-req-new-name" />
                  <Input value={form.report_to_phone} onChange={(e) => setForm({ ...form, report_to_phone: e.target.value })} placeholder="Phone number" data-testid="tr-req-new-phone" />
                  <Input value={form.report_to_designation} onChange={(e) => setForm({ ...form, report_to_designation: e.target.value })} placeholder="Designation (optional)" data-testid="tr-req-new-desg" />
                  <div className="col-span-1 md:col-span-3 text-[10px] text-indigo-700 dark:text-indigo-300">This number will be saved into the CRM Reporting contacts for this school.</div>
                </div>
              )}
              {!form.report_to_is_new && form.report_to_name && (
                <div className="text-[10.5px] text-emerald-700 dark:text-emerald-300">✓ Using existing CRM contact: {form.report_to_name}{form.report_to_phone ? ` · ${form.report_to_phone}` : ""}</div>
              )}
            </div>
          </F>

          <F label="Notes" full><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything the approver / assigned trainer should know" data-testid="tr-req-notes" /></F>
        </div>
        <DialogFooter className="px-6 pb-5 gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose}>{isReview ? "Close" : "Cancel"}</Button>
          {isReview ? (
            footerExtra
          ) : (
            <Button onClick={onSubmit} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="tr-req-submit" data-keep-color>
              <Send size={13} className="mr-1.5" /> Submit for Approval
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== Tab Access Chooser (Master Admin only, mirrors Incentive module) ==== */
function TabAccessChooser({ open, onClose, cfg, onSave }) {
  // v109 — Full matrix view: rows = users, columns = tab & sub-tab tags.
  // Click any tag to toggle that user's access. Master Admin always inherits.
  const [q, setQ] = useState("");
  const TAB_COLS = [
    { key: "trainer",             label: "Trainer",       tone: "indigo" },
    { key: "training",            label: "Training",      tone: "sky" },
    { key: "training_upcoming",   label: "· Upcoming",    tone: "slate", sub: true },
    { key: "training_assigned",   label: "· Assigned",    tone: "amber", sub: true },
    { key: "training_completed",  label: "· Completed",   tone: "emerald", sub: true },
    { key: "projects",            label: "Project & Activity", tone: "violet" },
    { key: "approval",            label: "Approval",      tone: "rose" },
  ];
  const toneStyles = {
    indigo:  { on: "bg-indigo-600 text-white ring-indigo-700",  off: "bg-white text-indigo-700 ring-indigo-200 hover:bg-indigo-50 dark:bg-slate-950 dark:text-indigo-300 dark:ring-indigo-500/30" },
    sky:     { on: "bg-sky-600 text-white ring-sky-700",        off: "bg-white text-sky-700 ring-sky-200 hover:bg-sky-50 dark:bg-slate-950 dark:text-sky-300 dark:ring-sky-500/30" },
    slate:   { on: "bg-slate-800 text-white ring-slate-900",    off: "bg-white text-slate-700 ring-slate-200 hover:bg-slate-50 dark:bg-slate-950 dark:text-slate-300 dark:ring-slate-700" },
    amber:   { on: "bg-amber-600 text-white ring-amber-700",    off: "bg-white text-amber-700 ring-amber-200 hover:bg-amber-50 dark:bg-slate-950 dark:text-amber-300 dark:ring-amber-500/30" },
    emerald: { on: "bg-emerald-600 text-white ring-emerald-700",off: "bg-white text-emerald-700 ring-emerald-200 hover:bg-emerald-50 dark:bg-slate-950 dark:text-emerald-300 dark:ring-emerald-500/30" },
    violet:  { on: "bg-violet-600 text-white ring-violet-700",  off: "bg-white text-violet-700 ring-violet-200 hover:bg-violet-50 dark:bg-slate-950 dark:text-violet-300 dark:ring-violet-500/30" },
    rose:    { on: "bg-rose-600 text-white ring-rose-700",      off: "bg-white text-rose-700 ring-rose-200 hover:bg-rose-50 dark:bg-slate-950 dark:text-rose-300 dark:ring-rose-500/30" },
  };
  if (!open) return null;
  const ta = cfg.tab_access || {};
  const users = (cfg.all_employees || []).filter((u) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (u.name || "").toLowerCase().includes(s) || (u.role || "").toLowerCase().includes(s);
  });
  const toggle = (uid, tabKey) => {
    const current = ((ta[tabKey] || []).map(String));
    const set = new Set(current);
    if (set.has(uid)) set.delete(uid);
    else set.add(uid);
    onSave(tabKey, Array.from(set));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="tr-tabaccess-matrix">
        <div className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <DialogTitle className="heading text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ShieldCheck size={18} className="text-indigo-700 dark:text-indigo-300" />
            Trainers Module · Tab Access Matrix
          </DialogTitle>
          <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1">Click a tag next to any user to grant/revoke access to that tab or sub-tab. Master Admin always has full access.</div>
        </div>
        <DialogHeader className="hidden"><DialogTitle>Access</DialogTitle></DialogHeader>
        <div className="px-6 py-3 border-b border-slate-100 dark:border-slate-800">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users by name or role…" className="pl-8 h-9 text-[12.5px]" data-testid="tr-tabaccess-search" />
          </div>
        </div>
        <div className="px-6 py-2 overflow-x-auto">
          <table className="w-full text-[11.5px]">
            <thead className="border-b border-slate-200 dark:border-slate-800 sticky top-0 bg-white dark:bg-slate-950">
              <tr>
                <th className="text-left px-2 py-2 font-semibold text-slate-600 dark:text-slate-300">User</th>
                {TAB_COLS.map((c) => (
                  <th key={c.key} className={`text-center px-1.5 py-2 font-semibold ${c.sub ? "text-slate-400" : "text-slate-600 dark:text-slate-300"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={TAB_COLS.length + 1} className="px-2 py-8 text-center text-slate-400 italic">No matching users.</td></tr>}
              {users.map((u) => (
                <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-900" data-testid={`tr-tabaccess-row-${u.id}`}>
                  <td className="px-2 py-2">
                    <div className="font-semibold text-slate-800 dark:text-slate-100">{u.name}</div>
                    <div className="text-[10.5px] text-slate-500">{u.role}</div>
                  </td>
                  {TAB_COLS.map((c) => {
                    const has = (ta[c.key] || []).map(String).includes(String(u.id));
                    const s = toneStyles[c.tone] || toneStyles.indigo;
                    return (
                      <td key={c.key} className="px-1 py-2 text-center">
                        <button
                          type="button"
                          onClick={() => toggle(u.id, c.key)}
                          className={`inline-flex items-center gap-1 h-6 px-2 rounded-full text-[10.5px] font-semibold ring-1 transition-colors ${has ? s.on : s.off}`}
                          data-testid={`tr-tabaccess-cell-${u.id}-${c.key}`}
                          data-keep-color
                        >
                          {has ? "✓ On" : "Off"}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <DialogFooter className="px-6 pb-5 pt-2 border-t border-slate-100 dark:border-slate-800">
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mr-auto">Changes save instantly per tag click.</div>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==== TAB 2 · Card view for Upcoming / Assigned / Completed sub-tabs ==== */
function AssignmentCards({ rows, isAdmin, isTrainer, user, onOpen, onEdit, onDelete, onRequest, variant }) {
  if (!rows || rows.length === 0) {
    return <div className="text-center py-12 text-slate-400 text-sm">No trainings here.</div>;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {rows.map((a) => {
        const requests = a.pending_requests || [];
        const myRequest = isTrainer && requests.find((r) => r.trainer_id === user?.id);
        const typeLabel = a.training_type === "Other" ? (a.type_other_name || "Other") : a.training_type;
        return (
          <div key={a.id} className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 flex flex-col gap-2 transition-colors hover:border-indigo-300 dark:hover:border-indigo-500/40" data-testid={`tr-card-${variant}-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{a.client_name}</div>
                {a.client_address && <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{a.client_address}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge className={`${STATUS_COLOR[a.status] || "bg-slate-100 text-slate-700"} text-[10px] font-semibold shrink-0`} data-keep-color>{a.status}</Badge>
                {variant === "upcoming" && requests.length > 0 && (
                  <Badge
                    className="bg-rose-600 text-white ring-1 ring-rose-700 text-[10px] font-bold animate-pulse"
                    data-keep-color
                    data-testid={`tr-c-pending-chip-${a.id}`}
                  >
                    ⚠ {requests.length} pending request{requests.length === 1 ? "" : "s"}
                  </Badge>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-[11.5px] text-slate-700 dark:text-slate-200 mt-1">
              <div><span className="text-slate-500 dark:text-slate-400">Type:</span> <span className="font-semibold">{typeLabel}</span></div>
              {variant !== "upcoming" && a.trainer_name && (
                <div><span className="text-slate-500 dark:text-slate-400">Trainer:</span> <span className="font-semibold">{a.trainer_name}</span></div>
              )}
              <div className="col-span-2 mono text-[11px] text-slate-600 dark:text-slate-300">{a.training_from} → {a.training_to}</div>
              {variant === "upcoming" && (
                <div className="col-span-2 text-[11px] text-slate-500 dark:text-slate-400">
                  {requests.length === 0 ? "No requests yet" : `${requests.length} pending request${requests.length === 1 ? "" : "s"}`}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 mt-2 flex-wrap">
              {variant === "upcoming" ? (
                isTrainer ? (
                  myRequest ? (
                    <Badge className="bg-amber-50 text-amber-700 ring-1 ring-amber-200 text-[10px]" data-testid={`tr-c-requested-${a.id}`}>You requested</Badge>
                  ) : (
                    <Button size="sm" onClick={() => onRequest(a)} className="h-8 text-xs !bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid={`tr-c-request-${a.id}`} data-keep-color>Request Training</Button>
                  )
                ) : (
                  <Button size="sm" variant="outline" onClick={() => onOpen(a)} className="h-8 text-xs" data-testid={`tr-c-open-${a.id}`}>Open</Button>
                )
              ) : (
                <Button size="sm" variant="outline" onClick={() => onOpen(a)} className="h-8 text-xs" data-testid={`tr-c-open-${a.id}`}>Open</Button>
              )}
              {isAdmin && variant === "upcoming" && onEdit && (
                <Button size="sm" variant="outline" onClick={() => onEdit(a)} className="h-8 text-xs" data-testid={`tr-c-edit-${a.id}`}>Edit</Button>
              )}
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => onDelete(a)} className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`tr-c-del-${a.id}`}>Remove</Button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function AssignmentTable({ rows, isAdmin, onOpen, onDelete, testidPrefix }) {
  const t = {
    border: "border-slate-200 dark:border-slate-800",
    bg: "bg-white dark:bg-slate-950",
    rowHover: "hover:bg-slate-50 dark:hover:bg-slate-900",
    hdrBorder: "border-slate-200 dark:border-slate-800",
    hdr: "bg-slate-50 dark:bg-slate-900",
    th: "slate",
    pillBg: "border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800",
  };
  return (
    <div className={`relative overflow-hidden rounded-xl border ${t.border} ${t.bg} overflow-x-auto`}>
      <table className="relative w-full text-sm responsive-table">
        <thead className={`border-b ${t.hdrBorder} ${t.hdr}`}>
          <tr>
            <Th tone={t.th}>Client</Th><Th tone={t.th}>Trainer</Th><Th tone={t.th}>Type</Th><Th tone={t.th}>Window</Th><Th tone={t.th}>Travel</Th><Th tone={t.th}>Status</Th>
            <Th tone={t.th} className="text-right">Action</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">No records.</td></tr>}
          {rows.map((a) => (
            <tr key={a.id} className={`border-b border-slate-100/60 ${t.rowHover} transition-colors`} data-testid={`tr-${testidPrefix}-${a.id}`}>
              <td className="px-4 py-3" data-label="Client">
                <div className="font-bold text-slate-900">{a.client_name}</div>
                <div className="text-[10px] text-slate-500 truncate max-w-[260px]">{a.client_address}</div>
              </td>
              <td className="px-4 py-3 text-[12px]" data-label="Trainer">{a.trainer_name}</td>
              <td className="px-4 py-3 text-[12px]" data-label="Type">{a.training_type === "Other" ? (a.type_other_name || "Other") : a.training_type}</td>
              <td className="px-4 py-3 mono text-[11px]" data-label="Window">{a.training_from} → {a.training_to}</td>
              <td className="px-4 py-3 text-[11px]" data-label="Travel">
                {(a.travel_dates?.length || 0) === 0 ? <span className="text-slate-400">—</span> : <Badge variant="secondary" className="text-[10px] ring-1 ring-slate-200">{a.travel_dates.length} dates</Badge>}
              </td>
              <td className="px-4 py-3" data-label="Status"><Badge className={`${STATUS_COLOR[a.status] || "bg-slate-100"} text-[10px] ring-1 ring-current/10`}>{a.status}</Badge></td>
              <td className="px-4 py-3 text-right" data-label="Action">
                <div className="flex items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => onOpen(a)} className={`h-8 text-xs bg-white ${t.pillBg}`} data-testid={`tr-open-${a.id}`}>Open</Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" onClick={() => onDelete(a)} className="h-8 text-xs text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`tr-delete-${a.id}`}>Remove</Button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function emptyForm() {
  return {
    client_id: "", trainer_id: "",
    training_type: "Demo", type_other_name: "",
    training_from: "", training_to: "",
    contact_name: "", contact_phone: "", contact_email: "",
    remark: "",
  };
}

const TH_TONE = {
  blue:    "text-blue-700",
  amber:   "text-amber-700",
  violet:  "text-violet-700",
  emerald: "text-emerald-700",
  sky:     "text-sky-700",
  slate:   "text-slate-700",
};
function Th({ children, className = "", tone = "slate" }) {
  return <th className={`px-4 py-3 text-left text-[10px] uppercase tracking-[0.16em] font-bold ${TH_TONE[tone]} ${className}`}>{children}</th>;
}
function MiniStat({ label, value }) {
  return (
    <div className="px-2 py-1.5 rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
      <div className="text-[9px] uppercase tracking-[0.14em] font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-[12px] font-bold mono text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}
function F({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function Info({ icon, label, value }) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 transition-all duration-200 hover:shadow-sm hover:border-slate-300 dark:hover:border-slate-700">
      <div className="text-[10px] uppercase tracking-wider font-bold text-indigo-700 dark:text-indigo-300 flex items-center gap-1.5">
        <span className="w-5 h-5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 flex items-center justify-center ring-1 ring-indigo-200 dark:ring-indigo-500/30">{icon}</span>
        {label}
      </div>
      <div className="text-[13px] text-slate-800 dark:text-slate-100 font-medium mt-1 break-words">{value || "—"}</div>
    </div>
  );
}

function DateWorkflowPanel({ a, userId, isAdmin, isTrainer, isMaster, onConfirm, onEditDates, onRequestChange, onDecide }) {
  const isOwner = isTrainer && a.trainer_id === userId;
  const confirmed = a.date_confirmation_status === "confirmed";
  const pending = a.date_confirmation_status === "pending_call";
  const editsLeft = isOwner ? Math.max(0, 1 - (a.trainer_edits_used || 0)) : null;
  const pendingReqs = (a.date_change_requests || []).filter((r) => r.status === "Pending");
  const history = (a.date_edits || []);
  const decided = (a.date_change_requests || []).filter((r) => r.status !== "Pending");

  // Don't render the panel for assignments without a trainer yet.
  if (!a.trainer_id) return null;

  return (
    <div className="border-t border-slate-200 pt-3 space-y-3" data-testid="tr-date-workflow">
      {/* Banner: pending confirmation */}
      {pending && (
        <div className="rounded-md px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap border border-amber-200 bg-amber-50 text-amber-900 dark:bg-amber-900/20 dark:border-amber-700 dark:text-amber-200" data-testid="tr-dw-pending-banner">
          <div className="text-[12px] leading-snug">
            <b>Action needed:</b> Please call the school to confirm the training window before doing anything else.
          </div>
          {(isOwner || isMaster) && (
            <Button size="sm" onClick={onConfirm} className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700 text-white" data-testid="tr-dw-confirm">
              Confirm Dates with School
            </Button>
          )}
        </div>
      )}

      {/* Status chip + edits-left indicator */}
      <div className="flex items-center gap-2 flex-wrap text-[11px]">
        {confirmed && (
          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100" data-testid="tr-dw-confirmed-chip">
            ✓ Dates confirmed{a.date_confirmed_by ? ` by ${a.date_confirmed_by}` : ""}
          </Badge>
        )}
        {isOwner && confirmed && (
          <Badge className={`${editsLeft > 0 ? "bg-sky-100 text-sky-800" : "bg-slate-100 text-slate-700"} hover:bg-sky-100`} data-testid="tr-dw-edits-left">
            {editsLeft > 0 ? `${editsLeft} direct edit left` : "Direct edit used"}
          </Badge>
        )}
        {pendingReqs.length > 0 && (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100" data-testid="tr-dw-pending-req-chip">
            {pendingReqs.length} date-change request{pendingReqs.length === 1 ? "" : "s"} pending
          </Badge>
        )}
      </div>

      {/* Action buttons (only after confirmation, and not on completed) */}
      {confirmed && a.status !== "Completed" && (
        <div className="flex items-center gap-2 flex-wrap">
          {(isOwner && editsLeft > 0) && (
            <Button size="sm" variant="outline" onClick={onEditDates} className="h-8 text-[11px]" data-testid="tr-dw-edit-btn">
              Edit Dates (1 left)
            </Button>
          )}
          {(isOwner && editsLeft === 0 && pendingReqs.length === 0) && (
            <Button size="sm" onClick={onRequestChange} className="h-8 text-[11px] bg-violet-600 hover:bg-violet-700 text-white" data-testid="tr-dw-request-btn">
              Request Date Change
            </Button>
          )}
          {isMaster && (
            <Button size="sm" variant="outline" onClick={onEditDates} className="h-8 text-[11px]" data-testid="tr-dw-admin-edit-btn">
              Edit Dates (Master Admin)
            </Button>
          )}
        </div>
      )}

      {/* Pending requests — only Master Admin can decide */}
      {pendingReqs.length > 0 && (
        <div className="space-y-2">
          {pendingReqs.map((r) => (
            <div key={r.id} className="border border-amber-200 dark:border-amber-700 rounded-md p-2.5 bg-amber-50/60 dark:bg-amber-900/10" data-testid={`tr-dw-req-${r.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="text-[12px]">
                  <div className="font-semibold text-slate-900 dark:text-slate-100">{r.requested_by_name}</div>
                  <div className="mono text-[11px] text-slate-600 dark:text-slate-300">{r.requested_from} → {r.requested_to}</div>
                  <div className="text-[11px] text-slate-700 dark:text-slate-300 mt-1"><b>Reason:</b> {r.reason}</div>
                  <div className="text-[10px] mono text-slate-400 mt-1">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</div>
                </div>
                {isMaster && (
                  <div className="flex items-center gap-1.5">
                    <Button size="sm" onClick={() => onDecide(r, "Approved")} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`tr-dw-req-approve-${r.id}`}>
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onDecide(r, "Rejected")} className="h-7 text-[11px] text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`tr-dw-req-reject-${r.id}`}>
                      Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Date history (collapsible) */}
      {(history.length > 0 || decided.length > 0) && (
        <details className="text-[11px] mt-1">
          <summary className="cursor-pointer text-slate-500 hover:text-slate-800 dark:hover:text-slate-200" data-testid="tr-dw-history-toggle">
            View date history ({history.length} edit{history.length === 1 ? "" : "s"}{decided.length ? `, ${decided.length} decided request${decided.length === 1 ? "" : "s"}` : ""})
          </summary>
          <ul className="mt-2 space-y-1.5 border-l-2 border-slate-200 dark:border-slate-700 pl-3">
            {[...history].reverse().map((h) => (
              <li key={h.id} className="text-[11px] text-slate-600 dark:text-slate-300" data-testid={`tr-dw-history-${h.id}`}>
                <span className="mono">{h.old_from} → {h.old_to}</span> ⇒{" "}
                <span className="mono font-semibold text-slate-900 dark:text-slate-100">{h.new_from} → {h.new_to}</span>
                <div className="text-[10px] text-slate-500">
                  by {h.by_name} ({h.by_role}) · {h.created_at ? new Date(h.created_at).toLocaleString() : ""} · {h.remark}
                </div>
              </li>
            ))}
            {decided.map((r) => (
              <li key={r.id} className="text-[11px] text-slate-600 dark:text-slate-300">
                <span className={r.status === "Approved" ? "text-emerald-700 dark:text-emerald-400" : "text-rose-700 dark:text-rose-400"}>
                  {r.status}:
                </span>{" "}
                Request {r.requested_from} → {r.requested_to} by {r.requested_by_name}
                {r.decided_remark && <span className="text-slate-500"> · {r.decided_remark}</span>}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}


/* ==== v108 · Approval Review Dialog (Open button in Approval tab) ==== */
function ApprovalReviewDialog({ req, onClose, canApproval, activeEmployees, trainers, onSaved }) {
  // v109.9.165 — Reuse the exact same form used to raise a training
  // request so both dialogs stay in lock-step. The only extra behaviour
  // here is Save / Approve / Reject and the pending-status metadata.
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!req) { setForm(null); return; }
    setForm({
      client_id: req.client_id || "",
      client_name: req.client_name || "",
      client_types: req.client_types || [],
      client_placeholder: !!req.client_placeholder,
      product_topic: req.product_topic || "",
      preferred_trainer_id: req.preferred_trainer_id || "",
      preferred_trainer_name: req.preferred_trainer_name || "",
      start_datetime: req.start_datetime || "",
      end_datetime: req.end_datetime || "",
      duration: req.duration || "",
      mode: req.mode || "physical",
      location: req.location || "",
      location_map_url: req.location_map_url || "",
      notes: req.notes || "",
      assigned_by_id: req.assigned_by_id || "",
      assigned_by_name: req.assigned_by_name || "",
      sales_associate_id: req.sales_associate_id || "",
      sales_associate_name: req.sales_associate_name || "",
      report_to_name: req.report_to_name || "",
      report_to_phone: req.report_to_phone || "",
      report_to_designation: req.report_to_designation || "",
      report_to_is_new: false,
    });
  }, [req]);
  if (!req || !form) return null;
  const isPending = req.status === "pending";
  const canEdit = canApproval && isPending;
  const save = async (afterAction) => {
    if (!canEdit) { afterAction && afterAction(); return; }
    setSaving(true);
    try {
      await api.patch(`/training-requests/${req.id}`, form);
      toast.success("Changes saved");
      if (afterAction) afterAction();
      else onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const approve = async () => {
    await save(async () => {
      try {
        // Placeholder request → prompt for the real CRM Client ID
        let extra = {};
        if (req.client_placeholder && !form.client_id) {
          const q = window.prompt("This request is a placeholder — paste the real CRM Client ID to link it to before approving.", "");
          if (!q || !q.trim()) { toast.error("Client ID is required"); return; }
          extra = { client_id: q.trim() };
        } else if (form.client_id && form.client_id !== req.client_id) {
          extra = { client_id: form.client_id };
        }
        await api.post(`/training-requests/${req.id}/approve`, extra);
        toast.success("Approved — visible in Upcoming Trainings");
        onSaved && onSaved();
      } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    });
  };
  const reject = async () => {
    const r = window.prompt("Rejection reason (required):", "");
    if (!r || !r.trim()) return;
    try {
      await api.post(`/training-requests/${req.id}/reject`, { reason: r.trim() });
      toast.success("Rejected");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const footer = (
    <>
      {canEdit && (
        <>
          <Button variant="outline" onClick={() => save()} disabled={saving} data-testid="tr-rv-save">{saving ? "Saving…" : "Save Changes"}</Button>
          <Button onClick={reject} className="text-rose-600 border border-rose-200 hover:bg-rose-50" variant="outline" data-testid="tr-rv-open-reject">Reject…</Button>
          <Button onClick={approve} className="!bg-emerald-600 hover:!bg-emerald-700 !text-white" data-testid="tr-rv-approve" data-keep-color>Approve</Button>
        </>
      )}
    </>
  );
  return (
    <TrainingRequestDialog
      open
      onClose={onClose}
      form={form}
      setForm={setForm}
      trainers={trainers}
      activeEmployees={activeEmployees}
      onSubmit={() => save()}
      mode="review"
      reviewMeta={{ client_name: req.client_name, status: req.status, created_by_name: req.created_by_name, created_at: req.created_at }}
      footerExtra={footer}
    />
  );
}


/* ==== v108 · Arrival Check-in Dialog (auto-modal for trainer) ==== */
function ArrivalCheckDialog({ assignment, onClose, onSaved }) {
  const [reached, setReached] = useState(null); // null | true | false
  const [leftHomeAt, setLeftHomeAt] = useState("");
  const [arrivedAt, setArrivedAt] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!assignment) { setReached(null); setLeftHomeAt(""); setArrivedAt(""); setReason(""); }
  }, [assignment]);
  if (!assignment) return null;
  const submit = async () => {
    if (reached === null) { toast.error("Please select Yes or No"); return; }
    if (reached && (!leftHomeAt || !arrivedAt)) { toast.error("Please provide both times"); return; }
    if (!reached && !reason.trim()) { toast.error("Please tell us why you couldn't reach the school"); return; }
    setSaving(true);
    try {
      await api.post(`/trainer-assignments/${assignment.id}/arrival-check`, {
        reached, left_home_at: leftHomeAt, arrived_at: arrivedAt, reason: reason.trim(),
      });
      toast.success(reached ? "Arrival recorded — training marked In Progress" : "Recorded — thanks for the update");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tr-arrival-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <MapPin size={16} className="text-indigo-700 dark:text-indigo-300" />
            Did you reach the school today?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="rounded-md border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-[12px]">
            <div className="font-semibold text-slate-900 dark:text-slate-100">{assignment.client_name}</div>
            <div className="text-[11px] text-slate-500">{assignment.training_from} → {assignment.training_to}</div>
            {assignment.location_map_url && (
              <a href={assignment.location_map_url} target="_blank" rel="noreferrer" className="text-[11px] text-indigo-700 dark:text-indigo-300 hover:underline mt-1 inline-block" data-testid="tr-arrival-map-link">Open location on Google Maps →</a>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setReached(true)}
              className={reached === true ? "!bg-emerald-600 !text-white" : "bg-white text-slate-800 border border-slate-200"}
              variant={reached === true ? "default" : "outline"}
              data-keep-color={reached === true ? "" : undefined}
              data-testid="tr-arrival-yes"
            >Yes, I reached</Button>
            <Button
              onClick={() => setReached(false)}
              className={reached === false ? "!bg-rose-600 !text-white" : "bg-white text-slate-800 border border-slate-200"}
              variant={reached === false ? "default" : "outline"}
              data-keep-color={reached === false ? "" : undefined}
              data-testid="tr-arrival-no"
            >No, I could not</Button>
          </div>
          {reached === true && (
            <div className="grid grid-cols-2 gap-2">
              <F label="Time you left home *">
                <Input type="time" value={leftHomeAt} onChange={(e) => setLeftHomeAt(e.target.value)} data-testid="tr-arrival-left" />
              </F>
              <F label="Time you arrived at school *">
                <Input type="time" value={arrivedAt} onChange={(e) => setArrivedAt(e.target.value)} data-testid="tr-arrival-arrived" />
              </F>
            </div>
          )}
          {reached === false && (
            <F label="Why couldn't you reach? *" full>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Traffic, health issue, school closed, etc." data-testid="tr-arrival-reason" />
            </F>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Later</Button>
          <Button onClick={submit} disabled={saving || reached === null} className="!bg-indigo-600 hover:!bg-indigo-700 !text-white" data-testid="tr-arrival-submit" data-keep-color>{saving ? "Saving…" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ==== v108 · Completion Upload Dialog ==== */
function CompletionUploadDialog({ assignment, onClose, onSaved }) {
  const [letter, setLetter] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  if (!assignment) return null;
  const submit = async () => {
    if (!letter) { toast.error("Completion Letter is required"); return; }
    if (!sheet) { toast.error("Attendance Sheet is required"); return; }
    if (!images || images.length === 0) { toast.error("At least one Training Image is required"); return; }
    const fd = new FormData();
    fd.append("completion_letter", letter);
    fd.append("attendance_sheet", sheet);
    for (const img of images) fd.append("training_images", img);
    setUploading(true);
    try {
      await api.post(`/trainer-assignments/${assignment.id}/complete-with-uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Training marked Completed. Files uploaded to Drive.");
      onSaved && onSaved();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="tr-complete-dialog">
        <DialogHeader>
          <DialogTitle className="heading flex items-center gap-2">
            <CheckCircle2 size={16} className="text-emerald-600" />
            Complete Training · Upload Verified Artefacts
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="text-[11.5px] text-slate-600 dark:text-slate-300">All three artefacts are uploaded to the Google Drive folder linked to this training.</div>
          <F label="Completion Letter (school-signed) *" full>
            <Input type="file" onChange={(e) => setLetter(e.target.files?.[0] || null)} data-testid="tr-complete-letter" />
            {letter && <div className="text-[11px] text-emerald-700 mt-1">✓ {letter.name}</div>}
          </F>
          <F label="Attendance Sheet Link (verified by school) *" full>
            <Input type="file" onChange={(e) => setSheet(e.target.files?.[0] || null)} data-testid="tr-complete-sheet" />
            {sheet && <div className="text-[11px] text-emerald-700 mt-1">✓ {sheet.name}</div>}
          </F>
          <F label="Training Images *" full>
            <Input type="file" multiple accept="image/*" onChange={(e) => setImages(Array.from(e.target.files || []))} data-testid="tr-complete-images" />
            {images.length > 0 && <div className="text-[11px] text-emerald-700 mt-1">✓ {images.length} image{images.length === 1 ? "" : "s"} selected</div>}
          </F>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={uploading} className="!bg-emerald-600 hover:!bg-emerald-700 !text-white" data-testid="tr-complete-submit" data-keep-color>
            {uploading ? "Uploading…" : "Upload & Mark Completed"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

